import {
  Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type PointerEvent as EventoDePonteiro,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { mensagemDoErro } from '../api';
import { data as fmtDataDoBanco, dataHora as fmtDataHora, plural } from '../formato';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Selo, Vazio,
} from '../ui';
import { useSessao } from '../sessao';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';
import {
  alocar, contaDatas, programar, verMaquinas, verRevisoes,
  type Barra, type Duracao, type Frente, type Maquina, type NaBandeja, type Resposta, type Revisao,
  type Movimento, type PropostaConflitos,
  gravarDuracao, verDuracoes, gravarCapacidade, proporConflitos, aplicarConflitos,
} from '../api/maquinas';
import { BarraDoCronograma, type AcaoDoMais } from './cronograma/BarraDoCronograma';
import {
  ATALHO_DA_ESCALA, PASSO_DO_ZOOM, ajustar, escalaDoCabecalho, gravarPreferencia, janelaDoZoom,
  lerPreferencia, limitarLargura, zoomNoCursor,
} from './cronograma/navegacao';
import {
  chaveDeRecolher, clienteCurto, recuoDoNivel, rotuloDaLinhaDaAmostra,
} from './cronograma/linhas';

/**
 * Programação de ensaios especiais — a linha do tempo por MÁQUINA.
 *
 * ── O que a Testesolo pediu ─────────────────────────────────────────
 *
 * «O ideal do cronograma é ser por equipamento, separado em
 *  Adensamentos, Triaxiais, Cisalhamento direto. Assim as amostras são
 *  alocadas para cada equipamento, podendo arrastar os blocos de
 *  ensaios para cada linha de equipamento, puxar para mudar a duração
 *  do ensaio (em dias).» — 02/09/2026.
 *
 * E, à pergunta sobre o que a barra significa: **«ocupação do
 * equipamento»**. A barra é a máquina PRESA.
 *
 * ── 0189: o programado e o realizado são duas camadas ──────────────
 *
 * Prompt do Gantt (22/09/2026), §2: «No LabManager, o real deve ser
 * representado separadamente do programado». A interface de referência
 * DESLOCA a barra para a data real quando o técnico começa; aqui a barra
 * do programado fica onde a promessa foi feita, e o real é uma segunda
 * barra, mais fina, por baixo — do primeiro dado bruto até a ficha
 * completa, ou até hoje, aberta, enquanto o ensaio corre. A previsão de
 * término (a duração programada contada do início real) é uma terceira
 * marca, tracejada, que se chama previsão.
 *
 * ── A forma da referência, adaptada ─────────────────────────────────
 *
 *   · uma LINHA POR ENSAIO, sob a máquina recolhível, com as colunas
 *     Início · Fim · Dur ao lado do nome (a pilha por posto da 0152
 *     virou lista: a Testesolo mostrou a lista);
 *   · cabeçalho por mês e por dia, fixo; a identificação fica parada
 *     enquanto o calendário rola;
 *   · zoom (largura do dia), «Hoje», e a janela em dias;
 *   · cartões: sem agendamento · agendados · em execução · concluídos ·
 *     atrasados — contando ENSAIOS, dentro do filtro e fora da janela;
 *   · no painel de detalhe: o histórico de REVISÕES do programado e a
 *     escolha de qual referência a régua tracejada mostra.
 *
 * ── Os arrastos, e o que cada um significa ──────────────────────────
 *
 *   · o corpo da barra, de lado → muda o INÍCIO, mantém a duração;
 *   · para cima ou para baixo → muda a MÁQUINA;
 *   · a alça da direita → muda a DURAÇÃO (o fim);
 *   · a alça da esquerda → muda o INÍCIO mantendo o FIM (a duração
 *     é recalculada);
 *   · da bandeja para uma faixa → a primeira alocação.
 *
 * Todos passam por UMA porta (`programar`, 0189), que grava data,
 * máquina e duração na mesma transação e pede motivo quando a data de
 * um ensaio já programado muda. As barras REAIS não se arrastam: o
 * arraste opera sobre o programado.
 */

/** A data, para ler — o `formato.data` lê a data-só pelo texto desde a onda 329. */
const fmtData = (v: string | null | undefined): string => fmtDataDoBanco(v);

const DIA_MS = 86400000;
/* A altura das linhas: a de sempre e a compacta (onda 378 — liga sozinha
   quando a altura útil da janela fica abaixo de 800 px, spec 15 §3.2). */
const ALTURA_LINHA = 40;
const ALTURA_CAB_MAQ = 34;
const ALTURA_COMPACTA = 28;

const emDias = (iso: string) => {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Math.round(Date.UTC(a, m - 1, d) / DIA_MS);
};
const paraIso = (dias: number) =>
  new Date(dias * DIA_MS).toISOString().slice(0, 10);

/* ── 0195 · O calendário do laboratório, na tela ─────────────────────
 *
 * A conta de dia útil é do banco (`fn_dia_util_do_laboratorio`, 0195 —
 * sábado, domingo e feriado do município da bancada). A tela repete a
 * MESMA conta com a lista de feriados que a API manda na resposta
 * (`feriados`), para o fantasma e a prévia responderem a cada movimento
 * do ponteiro sem uma ida ao servidor. As três funções são puras e
 * exportadas: o teste 196 as recorta e prova sem navegador, como a 201
 * fez com a relação colada.
 */

/** Sábado, domingo ou feriado da bancada. */
export const naoUtil = (iso: string, feriados: Set<string>) => {
  const dow = new Date(emDias(iso) * DIA_MS).getUTCDay();
  return dow === 0 || dow === 6 || feriados.has(iso);
};

/** O primeiro dia útil a partir de `iso`, inclusive. */
export const proximoDiaUtil = (iso: string, feriados: Set<string>) => {
  let d = emDias(iso);
  let guarda = 0;
  while (naoUtil(paraIso(d), feriados)) {
    d += 1;
    guarda += 1;
    if (guarda > 400) return paraIso(d);
  }
  return paraIso(d);
};

/**
 * Onde o chip pousa — RN-196.
 *
 * A coluna manda: solto sobre um dia FUTURO, o início é aquele dia,
 * levado ao próximo dia útil. Fora das colunas (ou sobre hoje/passado),
 * é o `proximo_inicio` da máquina — o próximo dia útil depois de ela
 * ficar livre, nunca hoje. Máquina sem `proximo_inicio` (impedida):
 * amanhã, no primeiro dia útil, e o servidor é quem recusa se for o caso.
 */
export const inicioDoArrasto = (p: {
  coluna: string | null; hoje: string; proximoInicio: string | null;
  feriados: Set<string>;
}): { inicio: string; por: 'coluna' | 'fila' } => {
  if (p.coluna && p.coluna > p.hoje) {
    return { inicio: proximoDiaUtil(p.coluna, p.feriados), por: 'coluna' };
  }
  const base = p.proximoInicio ?? paraIso(emDias(p.hoje) + 1);
  return { inicio: proximoDiaUtil(base, p.feriados), por: 'fila' };
};

/** O último dia ocupado por `dias` dias úteis a partir de `inicio` (útil). */
export const fimDaPrevia = (inicio: string, diasUteis: number, feriados: Set<string>) => {
  let d = emDias(proximoDiaUtil(inicio, feriados));
  let falta = Math.max(1, Math.ceil(diasUteis)) - 1;
  let guarda = 0;
  while (falta > 0 && guarda < 4000) {
    d += 1;
    guarda += 1;
    if (!naoUtil(paraIso(d), feriados)) falta -= 1;
  }
  return paraIso(d);
};

/**
 * A máquina serve para o chip? (0191)
 *
 * Pela categoria (ponte grupo → categoria, 0152) OU por exceção da própria
 * máquina (`grupos_extras`): a MS102 é «cisalhamento direto» e faz
 * adensamento. Sem categoria declarada para o grupo, não se impede.
 */
const serve = (maq: Maquina | undefined, chip: { categorias_aceitas: string[] | null; grupo: string | null } | undefined) => {
  if (!maq || !chip) return true;
  const aceita = chip.categorias_aceitas;
  if (!aceita || !aceita.length) return true;
  if (aceita.includes(maq.categoria)) return true;
  return !!chip.grupo && (maq.grupos_extras ?? []).includes(chip.grupo);
};

/**
 * 0197: a frente serve para o chip? Só ensaio trivial vai para frente, e
 * vai para a frente que o MÉTODO declara (`frente_metodo`); método trivial
 * sem frente declarada aceita qualquer uma.
 */
const serveFrente = (fr: Frente | undefined, chip: { classe?: string; frente_sugerida_id?: string | null } | undefined) => {
  if (!fr || !chip) return false;
  if (chip.classe !== 'trivial') return false;
  return !chip.frente_sugerida_id || chip.frente_sugerida_id === fr.id;
};

/**
 * 0197: o próximo início numa FRENTE — a mesma regra da máquina (RN-196:
 * nunca hoje, sempre dia útil), só que o «posto» é a capacidade por dia:
 * o primeiro dia útil a partir de amanhã em que a carga é menor que ela.
 */
export const proximoInicioDaFrente = (p: {
  hoje: string; capacidade: number; carga: Map<string, number>; feriados: Set<string>;
}) => {
  let d = proximoDiaUtil(paraIso(emDias(p.hoje) + 1), p.feriados);
  for (let i = 0; i < 400; i += 1) {
    if ((p.carga.get(d) ?? 0) < p.capacidade) return d;
    d = proximoDiaUtil(paraIso(emDias(d) + 1), p.feriados);
  }
  return d;
};

const FAMILIA: Record<Frente['familia'], string> = {
  caracterizacao: 'Caracterização',
  compactacao: 'Compactação, CBR e permeabilidade',
};

const hojeNavegador = () => new Date().toLocaleDateString('en-CA');

const DIA_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const num = (v: string | number | null | undefined) =>
  v == null ? 0 : Number(v);

/** «4,5 d» — vírgula, porque a interface é em português (§3). */
const dias = (v: string | number | null | undefined) => {
  const n = num(v);
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} d`;
};

const ORIGEM: Record<Revisao['origem'], string> = {
  formulario: 'formulário', gantt: 'Gantt', lote: 'lote',
  grade: 'grade da OS', sistema: 'sistema',
};

const SITUACAO: Record<string, string> = {
  programado: 'programado', em_execucao: 'em execução', pausado: 'pausado',
  dados_completos: 'ficha completa', em_processamento: 'em processamento',
  aguardando_verificacao: 'aguardando verificação',
  devolvido_para_correcao: 'devolvido para correção', aprovado: 'aprovado',
};

interface Arrasto {
  tipo: 'mover' | 'duracao' | 'inicio' | 'bandeja';
  ensaioId: string;
  origemX: number;
  origemY: number;
  dx: number;
  dy: number;
  barra?: Barra;
  chip?: NaBandeja;
}

/** O que «Desfazer» devolve: o programado como estava antes do gesto. */
interface Anterior {
  ensaioId: string; rotulo: string;
  inicio: string; duracaoDias: number | null; equipamentoId: string | null;
  fds: boolean;
}

export function ProgramacaoMaquinas() {
  const { pode } = useSessao();
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [desfazer, setDesfazer] = useState<Anterior | null>(null);
  const [gravando, setGravando] = useState(false);

  /*
   * 304 (RN-249): o cronograma da OS manda para cá com `?os=<id>` (e
   * `?de=AAAA-MM-DD`, o primeiro dia dos ensaios dela) — «Abrir no
   * cronograma geral →». A aba da OS só lê; mudar data é aqui, onde a
   * reprogramação guarda o motivo e a data original. O endereço só ABRE
   * com o filtro: depois, os filtros são da tela, como sempre foram.
   */
  const [params] = useSearchParams();
  const [de, setDe] = useState(() => {
    const p = params.get('de');
    return p && /^\d{4}-\d{2}-\d{2}$/.test(p) ? p : hojeNavegador();
  });
  /* 0195: abre com 90 dias; estende até 120 (o servidor aceita 180). */
  const [janela, setJanela] = useState(90);
  /*
   * 378: o que o APARELHO lembra (bandeja, zoom, «ocultar linhas vazias») —
   * `localStorage` na chave `cronograma.v1`, lido uma vez; sem armazenamento,
   * o padrão (spec 15 §3.2).
   */
  const [preferencia] = useState(() => lerPreferencia(
    typeof window === 'undefined' ? null : (() => { try { return window.localStorage; } catch { return null; } })()));
  const [larguraDia, setLarguraDia] = useState(preferencia.larguraDia);
  const [metodo, setMetodo] = useState('');
  const [busca, setBusca] = useState('');
  const [os, setOs] = useState(() => params.get('os') ?? '');
  const [cliente, setCliente] = useState('');
  const [soCritico, setSoCritico] = useState(false);
  /* 0197: todos, só especiais (máquinas) ou só triviais (frentes). */
  const [classe, setClasse] = useState<'' | 'especial' | 'trivial'>('');
  const [bandejaAberta, setBandejaAberta] = useState(preferencia.bandejaAberta);
  /* 378: grupo, equipamento e frente recolhem — a chave diz qual (`chaveDeRecolher`). */
  const [recolhidas, setRecolhidas] = useState<Set<string>>(new Set());
  const [ocultarVazias, setOcultarVazias] = useState(preferencia.ocultarVazias);
  const [telaCheia, setTelaCheia] = useState(false);
  const [maisAberto, setMaisAberto] = useState(false);
  const [compacta, setCompacta] = useState(false);
  /* A largura à vista da linha do tempo (sem a coluna dos nomes): o zoom
     afastado pede mais dias ao servidor (`janelaDoZoom`). */
  const [larguraVisivel, setLarguraVisivel] = useState(0);

  const [novaProg, setNovaProg] = useState<{
    ensaioId: string; equipamentoId: string; frenteId: string; data: string;
    duracao: string; fds: boolean; obs: string;
  } | null>(null);
  const [setas, setSetas] = useState<{
    de: string; para: string; x1: number; y1: number; x2: number; y2: number;
  }[]>([]);
  const corpo = useRef<HTMLDivElement>(null);
  const regua = useRef<HTMLDivElement>(null);

  const [arrasto, setArrasto] = useState<Arrasto | null>(null);
  /*
   * 0195: o gesto inteiro numa referência. O efeito do arrasto (abaixo)
   * dependia do OBJETO `arrasto`, que muda a cada movimento do ponteiro —
   * e a cada mudança o React desmontava o efeito (apagando o alvo) e
   * montava outro. Enquanto o ponteiro andava, o movimento seguinte
   * reacendia a linha; parado, ela ficava apagada. Era isso que o
   * Humberto via: «a linha não está sendo highlighted». O efeito agora
   * vive do começo ao fim do gesto, e lê o estado atual por aqui.
   */
  const arrastoRef = useRef<Arrasto | null>(null);
  arrastoRef.current = arrasto;
  /* 0191: a máquina sob o ponteiro enquanto se arrasta um chip — a linha
     dela acende. E se houve arrasto de verdade, o clique que o navegador
     dispara no fim não abre o formulário. */
  const [alvo, setAlvo] = useState<string | null>(null);
  /* 0195: a coluna (dia) sob o ponteiro durante o arrasto do chip. */
  const [colunaAlvo, setColunaAlvo] = useState<number | null>(null);
  /* 0197: a frente sob o ponteiro (exclusiva com `alvo`, a máquina). */
  const [alvoFrente, setAlvoFrente] = useState<string | null>(null);
  const [capacidades, setCapacidades] = useState(false);
  /* 0198: o diálogo «Resolver conflitos» e o desfazer do lote gravado. */
  const [resolvendo, setResolvendo] = useState<{ equipamentoId?: string; ensaioId?: string } | null>(null);
  const [desfazerLote, setDesfazerLote] = useState<{ ensaioId: string; inicioAnterior: string; inicioNovo: string }[] | null>(null);
  const arrastou = useRef(false);
  const [duracoes, setDuracoes] = useState<Duracao[] | null>(null);
  const [olhando, setOlhando] = useState<Barra | null>(null);
  const [pedindoMotivo, setPedindoMotivo] = useState<{
    barra: Barra; novoInicio: string; novoFim?: string;
    equipamentoId: string | null;
  } | null>(null);
  const [motivo, setMotivo] = useState('');

  /* 266 · opção A: contadores que filtram, filtros num painel, legenda num
     painel, a bandeja agrupada e procurável, e marcar vários para o lote. */
  const [situacao, setSituacao] = useState<'' | 'sem_agendamento' | 'agendados' | 'em_execucao' | 'concluidos' | 'atrasados'>('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [legendaAberta, setLegendaAberta] = useState(false);
  const [buscaBandeja, setBuscaBandeja] = useState('');
  const [agrupar, setAgrupar] = useState<'os' | 'metodo'>('os');
  const [gruposFechados, setGruposFechados] = useState<Set<string>>(new Set());
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [emLote, setEmLote] = useState(false);

  /* Esc fecha o painel de filtros, o da legenda e o ⋯ (os três flutuam). */
  useEffect(() => {
    if (!filtrosAbertos && !legendaAberta && !maisAberto) return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFiltrosAbertos(false); setLegendaAberta(false); setMaisAberto(false); }
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [filtrosAbertos, legendaAberta, maisAberto]);

  /* 378: o aparelho lembra a bandeja, o zoom e a chave das linhas vazias. */
  useEffect(() => {
    let armazem: Storage | null = null;
    try { armazem = window.localStorage; } catch { armazem = null; }
    gravarPreferencia(armazem, { bandejaAberta, larguraDia, ocultarVazias });
  }, [bandejaAberta, larguraDia, ocultarVazias]);

  /*
   * 378 · a tela cheia (tecla F e botão): uma classe no <body> esconde a
   * moldura (topo, menu, trilha) e a barra cai para 40 px; Esc ou F sai.
   */
  useEffect(() => {
    document.body.classList.toggle('cronograma-tela-cheia', telaCheia);
    return () => document.body.classList.remove('cronograma-tela-cheia');
  }, [telaCheia]);

  const hoje = dados?.hoje ?? hojeNavegador();
  const LARGURA_DIA = larguraDia;
  /* 378: a janela que se pede — a escolhida, ou mais quando o zoom mostra mais dias. */
  const janelaEfetiva = janelaDoZoom(janela, larguraVisivel, larguraDia);
  const alturaLinha = compacta ? ALTURA_COMPACTA : ALTURA_LINHA;
  const alturaCab = compacta ? ALTURA_COMPACTA : ALTURA_CAB_MAQ;

  // ── carregar ──────────────────────────────────────────────────────

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setDados(await verMaquinas({
        de, dias: janelaEfetiva, metodo, busca, os, cliente, so_critico: soCritico,
        classe,
      }));
    } catch (e) { setErro(e); }
  }, [de, janelaEfetiva, metodo, busca, os, cliente, soCritico, classe]);

  useEffect(() => { void carregar(); }, [carregar]);

  const inicio = emDias(de);
  const hojeDias = emDias(hoje);
  const colunas = janelaEfetiva;
  const x = (iso: string) => (emDias(iso) - inicio) * LARGURA_DIA;

  /* 0195: os feriados da bancada na janela, como conjunto de `AAAA-MM-DD`. */
  const feriados = useMemo(
    () => new Map((dados?.feriados ?? []).map((f) => [f.dia, f.nome])),
    [dados]);
  const feriadosSet = useMemo(() => new Set(feriados.keys()), [feriados]);

  /*
   * 0195: a prévia — onde e quando o chip arrastado vai pousar, a cada
   * movimento do ponteiro. É a mesma regra que o soltar aplica (RN-196),
   * por isso o que se vê é o que se grava.
   */
  /* 0197: a carga de cada frente por dia, como mapa `AAAA-MM-DD` → n. */
  const cargas = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    for (const f of dados?.frentes ?? []) {
      m.set(f.id, new Map(f.carga.map((c) => [c.dia, c.n])));
    }
    return m;
  }, [dados]);

  const previa = useMemo(() => {
    if (arrasto?.tipo !== 'bandeja' || !arrasto.chip) return null;
    const dur = Math.max(1, Number(arrasto.chip.duracao_sugerida) || 1);
    const coluna = colunaAlvo == null ? null : paraIso(inicio + colunaAlvo);
    if (alvo) {
      const maq = dados?.maquinas.find((m) => m.equipamento_id === alvo);
      if (!maq || !serve(maq, arrasto.chip) || arrasto.chip.classe === 'trivial') return null;
      const { inicio: ini, por } = inicioDoArrasto({
        coluna, hoje, proximoInicio: maq.proximo_inicio, feriados: feriadosSet,
      });
      return { maquina: maq.equipamento_id, frente: null as string | null, inicio: ini, fim: fimDaPrevia(ini, dur, feriadosSet), dur, por };
    }
    if (alvoFrente) {
      /* 0197: na frente, a fila é a capacidade do dia. */
      const fr = dados?.frentes.find((f) => f.id === alvoFrente);
      if (!fr || !serveFrente(fr, arrasto.chip)) return null;
      const { inicio: ini, por } = inicioDoArrasto({
        coluna, hoje, feriados: feriadosSet,
        proximoInicio: proximoInicioDaFrente({
          hoje, capacidade: fr.capacidade_dia,
          carga: cargas.get(fr.id) ?? new Map(), feriados: feriadosSet,
        }),
      });
      return { maquina: null as string | null, frente: fr.id, inicio: ini, fim: fimDaPrevia(ini, dur, feriadosSet), dur, por };
    }
    return null;
  }, [arrasto, alvo, alvoFrente, colunaAlvo, dados, inicio, hoje, feriadosSet, cargas]);

  /* 0197: as barras por frente — os triviais, uma linha por ensaio. */
  const porFrente = useMemo(() => {
    const m = new Map<string, Barra[]>();
    for (const b of dados?.barras ?? []) {
      if (!b.frente_id) continue;
      const lista = m.get(b.frente_id) ?? [];
      lista.push(b);
      m.set(b.frente_id, lista);
    }
    for (const lista of m.values()) {
      lista.sort((a, b) => a.inicio_previsto.localeCompare(b.inicio_previsto)
        || a.amostra_codigo.localeCompare(b.amostra_codigo));
    }
    return m;
  }, [dados]);

  /* 0197: as frentes por família, na ordem do cadastro. */
  const familias = useMemo(() => {
    const g: { familia: Frente['familia']; frentes: Frente[] }[] = [];
    for (const f of dados?.frentes ?? []) {
      const u = g[g.length - 1];
      if (u && u.familia === f.familia) u.frentes.push(f);
      else g.push({ familia: f.familia, frentes: [f] });
    }
    return g;
  }, [dados]);

  /* As barras por máquina, em lista (uma linha por ensaio). */
  const porMaquina = useMemo(() => {
    const m = new Map<string, Barra[]>();
    for (const b of dados?.barras ?? []) {
      if (!b.equipamento_id) continue;
      const lista = m.get(b.equipamento_id) ?? [];
      lista.push(b);
      m.set(b.equipamento_id, lista);
    }
    for (const lista of m.values()) {
      lista.sort((a, b) => a.inicio_previsto.localeCompare(b.inicio_previsto)
        || a.amostra_codigo.localeCompare(b.amostra_codigo));
    }
    return m;
  }, [dados]);

  /* Os grupos de família — «Adensamentos, Triaxiais, Cisalhamento». */
  const grupos = useMemo(() => {
    const g: { nome: string; maquinas: Maquina[] }[] = [];
    for (const m of dados?.maquinas ?? []) {
      const ultimo = g[g.length - 1];
      if (ultimo && ultimo.nome === m.categoria_nome) ultimo.maquinas.push(m);
      else g.push({ nome: m.categoria_nome, maquinas: [m] });
    }
    return g;
  }, [dados]);


  /*
   * Mede as setas a partir das barras já desenhadas. Roda a cada dado
   * novo, a cada arrasto e a cada rolagem, porque a barra que se move
   * leva a seta junto.
   */
  const medirSetas = useCallback(() => {
    const raiz = corpo.current;
    if (!raiz || !dados) { setSetas([]); return; }
    const r0 = raiz.getBoundingClientRect();
    const pos = new Map<string, { x: number; y: number; w: number }>();
    raiz.querySelectorAll<HTMLElement>('[data-ensaio]').forEach((el) => {
      const r = el.getBoundingClientRect();
      pos.set(el.dataset.ensaio!, {
        x: r.left - r0.left + raiz.scrollLeft,
        y: r.top - r0.top + raiz.scrollTop + r.height / 2,
        w: r.width,
      });
    });
    const novas: typeof setas = [];
    for (const b of dados.barras) {
      for (const dep of b.depende_de ?? []) {
        const a = pos.get(dep), z = pos.get(b.ensaio_id);
        if (!a || !z) continue;   // predecessor fora da janela, ou sem máquina
        novas.push({ de: dep, para: b.ensaio_id,
          x1: a.x + a.w, y1: a.y, x2: z.x - 3, y2: z.y });
      }
    }
    setSetas(novas);
  }, [dados]);

  useLayoutEffect(() => { medirSetas(); }, [medirSetas, arrasto, soCritico, recolhidas, larguraDia, compacta, situacao, ocultarVazias]);

  // ── 378 · a navegação (spec 15 §3.5, D4) ──────────────────────────────
  //
  // rodinha: rola as linhas (o do navegador) · Shift + rodinha: os dias ·
  // Ctrl + rodinha, pinça, + −, régua: zoom que fixa o dia sob o cursor ·
  // botão do meio ou espaço + arrastar: move a tela · o botão esquerdo só
  // pega as barras (fora delas, nada se move).

  const larguraRef = useRef(larguraDia);
  larguraRef.current = larguraDia;
  /* A rolagem que o zoom quer: aplicada depois de a grade nova ser desenhada. */
  const rolagemPendente = useRef<number | null>(null);
  const espaco = useRef(false);
  const [movendo, setMovendo] = useState(false);
  const panRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  const flutuando = useRef(false);
  flutuando.current = filtrosAbertos || legendaAberta || maisAberto;
  const telaCheiaRef = useRef(telaCheia);
  telaCheiaRef.current = telaCheia;

  /** A largura da coluna dos nomes (fica parada; a linha do tempo começa depois dela). */
  const larguraDosNomes = () =>
    (corpo.current?.querySelector('.gm-cabeca .gm-esq') as HTMLElement | null)?.offsetWidth ?? 0;

  /** Zoom pelo fator, fixando o dia sob `xCliente` (sem ele, o meio da grade). */
  const aplicarZoom = useCallback((fator: number, xCliente?: number) => {
    const raiz = corpo.current;
    if (!raiz) { setLarguraDia((l) => limitarLargura(l * fator)); return; }
    const esq = (raiz.querySelector('.gm-cabeca .gm-esq') as HTMLElement | null)?.offsetWidth ?? 0;
    const visivel = Math.max(1, raiz.clientWidth - esq);
    const origem = raiz.getBoundingClientRect().left + esq;
    const xc = xCliente == null ? visivel / 2 : Math.max(0, Math.min(visivel, xCliente - origem));
    const z = zoomNoCursor(larguraRef.current, fator, xc, rolagemPendente.current ?? raiz.scrollLeft);
    larguraRef.current = z.larguraDia;
    rolagemPendente.current = z.rolagem;
    setLarguraDia(z.larguraDia);
  }, []);

  useLayoutEffect(() => {
    const raiz = corpo.current;
    if (raiz && rolagemPendente.current != null) {
      raiz.scrollLeft = rolagemPendente.current;
      rolagemPendente.current = null;
    }
  }, [larguraDia, dados]);

  const temGrade = !!dados && (dados.maquinas.length > 0 || dados.frentes.length > 0);

  /* A rodinha e a pinça: ouvintes não passivos (o zoom cancela o do navegador). */
  useEffect(() => {
    const raiz = corpo.current;
    if (!raiz) return undefined;
    const roda = (e: WheelEvent) => {
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        aplicarZoom(Math.exp(-dy * 0.0015), e.clientX);
        return;
      }
      if (e.shiftKey && Math.abs(e.deltaX) < Math.abs(dy)) {
        e.preventDefault();
        raiz.scrollLeft += dy;
      }
    };
    let pinca: { dist: number } | null = null;
    const distancia = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const toca = (e: TouchEvent) => { pinca = e.touches.length === 2 ? { dist: distancia(e.touches) } : null; };
    const arrastaDedos = (e: TouchEvent) => {
      if (!pinca || e.touches.length !== 2) return;
      e.preventDefault();
      const d = distancia(e.touches);
      if (pinca.dist > 0) aplicarZoom(d / pinca.dist, (e.touches[0].clientX + e.touches[1].clientX) / 2);
      pinca.dist = d;
    };
    const solta = (e: TouchEvent) => { if (e.touches.length < 2) pinca = null; };
    raiz.addEventListener('wheel', roda, { passive: false });
    raiz.addEventListener('touchstart', toca, { passive: true });
    raiz.addEventListener('touchmove', arrastaDedos, { passive: false });
    raiz.addEventListener('touchend', solta);
    raiz.addEventListener('touchcancel', solta);
    return () => {
      raiz.removeEventListener('wheel', roda);
      raiz.removeEventListener('touchstart', toca);
      raiz.removeEventListener('touchmove', arrastaDedos);
      raiz.removeEventListener('touchend', solta);
      raiz.removeEventListener('touchcancel', solta);
    };
  }, [temGrade, aplicarZoom]);

  /* As teclas: F (tela cheia), Esc (sai), + − (zoom) e o espaço (segurar para mover). */
  useEffect(() => {
    const ehCampo = (t: EventTarget | null) => t instanceof HTMLElement
      && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
    const desce = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (ehCampo(e.target) || document.querySelector('dialog[open]')) return;
      if (e.key === 'f' || e.key === 'F') { e.preventDefault(); setTelaCheia((v) => !v); return; }
      if (e.key === 'Escape') {
        if (telaCheiaRef.current && !flutuando.current) setTelaCheia(false);
        return;
      }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); aplicarZoom(PASSO_DO_ZOOM); return; }
      if (e.key === '-' || e.key === '_') { e.preventDefault(); aplicarZoom(1 / PASSO_DO_ZOOM); return; }
      if (e.key === ' ' && !(e.target instanceof HTMLButtonElement) && !(e.target instanceof HTMLAnchorElement)) {
        e.preventDefault();
        if (!espaco.current) { espaco.current = true; setMovendo(true); }
      }
    };
    const sobe = (e: KeyboardEvent) => {
      if (e.key === ' ' && espaco.current) { espaco.current = false; setMovendo(false); }
    };
    const perdeFoco = () => { espaco.current = false; setMovendo(false); };
    document.addEventListener('keydown', desce);
    document.addEventListener('keyup', sobe);
    window.addEventListener('blur', perdeFoco);
    return () => {
      document.removeEventListener('keydown', desce);
      document.removeEventListener('keyup', sobe);
      window.removeEventListener('blur', perdeFoco);
    };
  }, [aplicarZoom]);

  /* Mover a tela: botão do meio (sem o rolar automático do navegador) ou espaço + esquerdo. */
  const comecarAMover = (e: EventoDePonteiro<HTMLDivElement>) => {
    const raiz = corpo.current;
    if (!raiz) return;
    if (!(e.button === 1 || (e.button === 0 && espaco.current))) return;
    e.preventDefault();
    try { raiz.setPointerCapture(e.pointerId); } catch { /* sem captura */ }
    panRef.current = { x: e.clientX, y: e.clientY, sl: raiz.scrollLeft, st: raiz.scrollTop };
    setMovendo(true);
  };
  const mover = (e: EventoDePonteiro<HTMLDivElement>) => {
    const raiz = corpo.current;
    const pan = panRef.current;
    if (!raiz || !pan) return;
    raiz.scrollLeft = pan.sl - (e.clientX - pan.x);
    raiz.scrollTop = pan.st - (e.clientY - pan.y);
  };
  const pararDeMover = () => {
    if (!panRef.current) return;
    panRef.current = null;
    setMovendo(espaco.current);
    medirSetas();
  };

  /*
   * A altura: a área ocupa o que sobra da janela até embaixo (a página não
   * rola; rola a grade), e a densidade compacta liga com altura útil < 800 px.
   */
  const area = useRef<HTMLDivElement>(null);
  const medirATela = useRef(() => {});
  medirATela.current = () => {
    const topo = telaCheia ? 0 : (document.querySelector('.topo')?.getBoundingClientRect().height ?? 48);
    setCompacta(window.innerHeight - topo < 800);
    const el = area.current;
    if (el) {
      if (window.innerWidth <= 900) el.style.removeProperty('--gm-altura');
      else {
        const topoDaArea = el.getBoundingClientRect().top + window.scrollY;
        let h = Math.max(416, Math.floor(window.innerHeight - topoDaArea - 8));
        el.style.setProperty('--gm-altura', `${h}px`);
        const sobra = document.documentElement.scrollHeight - window.innerHeight;
        if (sobra > 0 && h - sobra >= 416) { h -= sobra; el.style.setProperty('--gm-altura', `${h}px`); }
      }
    }
    const raiz = corpo.current;
    if (raiz) setLarguraVisivel(Math.max(0, raiz.clientWidth - larguraDosNomes()));
  };
  useLayoutEffect(() => { medirATela.current(); });
  useEffect(() => {
    const r = () => medirATela.current();
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  // ── o arrasto ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!arrasto) return;
    const mover = (e: PointerEvent) => {
      setArrasto((a) => (a ? {
        ...a, dx: e.clientX - a.origemX, dy: e.clientY - a.origemY,
      } : a));
      /*
       * 0197: houve arrasto de verdade? Vale para a BARRA também — o clique
       * que o navegador dispara depois do pointerup abria o painel do
       * ensaio por cima do pedido de motivo («abre uma janela completa…
       * obrigado a clicar no X e só depois aparece a janela para
       * justificar»). O `concluir` já zerou `arrasto` quando o clique
       * chega; a referência é o que sobra para dizer que foi um gesto.
       */
      if (Math.abs(e.clientX - arrasto.origemX) + Math.abs(e.clientY - arrasto.origemY) > 4) {
        arrastou.current = true;
      }
      /*
       * 0198: a BARRA arrastada também mira uma máquina — «arrastar a
       * linha ou barra de programação de uma amostra já programada para
       * atribuir a outro equipamento». A gravação já existia (0152); o que
       * faltava era a tela mostrar: a linha alvo acesa e o fantasma.
       */
      if (arrasto.tipo === 'bandeja' || (arrasto.tipo === 'mover' && arrasto.barra?.equipamento_id)) {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        setAlvo(el?.closest('[data-maquina]')?.getAttribute('data-maquina') ?? null);
        /* 0197: ou uma frente (só o chip). */
        setAlvoFrente(arrasto.tipo === 'bandeja'
          ? (el?.closest('[data-frente]')?.getAttribute('data-frente') ?? null) : null);
        /* 0195: e a coluna — pela régua, que rola junto com as faixas. */
        const g = regua.current;
        if (g) {
          const r = g.getBoundingClientRect();
          const c = Math.floor((e.clientX - r.left) / LARGURA_DIA);
          setColunaAlvo(c >= 0 && c < colunas ? c : null);
        }
      }
    };
    const soltar = (e: PointerEvent) => { void concluir(e); };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar, { once: true });
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      setAlvo(null);
      setAlvoFrente(null);
      setColunaAlvo(null);
    };
    /* Só o COMEÇO e o FIM do gesto montam e desmontam o efeito (0195). */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrasto?.tipo, arrasto?.ensaioId]);

  /** Que máquina está embaixo do ponteiro. */
  const maquinaSob = (e: PointerEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const faixa = el?.closest('[data-maquina]');
    return faixa?.getAttribute('data-maquina') ?? null;
  };

  /** 0197: que frente está embaixo do ponteiro. */
  const frenteSob = (e: PointerEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    return el?.closest('[data-frente]')?.getAttribute('data-frente') ?? null;
  };

  const colunaSob = (e: PointerEvent) => {
    const g = regua.current;
    if (!g) return null;
    const r = g.getBoundingClientRect();
    const c = Math.floor((e.clientX - r.left) / LARGURA_DIA);
    return c >= 0 && c < colunas ? c : null;
  };

  /* RN-248 (onda 303): o nome do cliente, o código e a OS — o cronograma
     de máquinas mistura amostras de todas as OS. */
  const rotuloDe = (b: { metodo_codigo: string; amostra_codigo: string;
    identificacao_recebida?: string | null; os_numero?: string | null }) =>
    `${b.metodo_codigo} · ${nomeDaAmostra(daApi(b, true))}`;

  const lembrar = (b: Barra) => setDesfazer({
    ensaioId: b.ensaio_id, rotulo: rotuloDe(b),
    inicio: b.inicio_previsto,
    duracaoDias: b.duracao_do_metodo ? null : num(b.duracao_dias),
    equipamentoId: b.equipamento_id, fds: b.corre_no_fim_de_semana,
  });

  const concluir = async (e: PointerEvent) => {
    const a = arrastoRef.current;   // 0195: o gesto como está AGORA, não o do closure
    setArrasto(null);
    if (!a) return;
    setRecado(null); setOk(null);

    const passos = Math.round(a.dx / LARGURA_DIA);
    const alvo = maquinaSob(e);
    const frente = frenteSob(e);

    try {
      if (a.tipo === 'bandeja' && a.chip && !alvo && frente) {
        /*
         * 0197: o chip caiu numa FRENTE. Só trivial, e só na frente do
         * método; a data é a coluna, ou o primeiro dia útil com vaga na
         * capacidade da frente (a mesma regra da máquina, RN-196).
         */
        const fr = dados?.frentes.find((f) => f.id === frente);
        if (!fr) return;
        if (a.chip.classe !== 'trivial') {
          setRecado(`${a.chip.metodo_codigo} é ensaio especial: ocupa uma máquina, não uma frente.`);
          return;
        }
        if (!serveFrente(fr, a.chip)) {
          setRecado(`${a.chip.metodo_codigo} é da frente ${a.chip.frente_sugerida_codigo ?? '?'}, não da ${fr.codigo}.`);
          return;
        }
        const coluna = colunaSob(e);
        const { inicio: dataAlvo } = inicioDoArrasto({
          coluna: coluna == null ? null : paraIso(inicio + coluna),
          hoje, feriados: feriadosSet,
          proximoInicio: proximoInicioDaFrente({
            hoje, capacidade: fr.capacidade_dia,
            carga: cargas.get(fr.id) ?? new Map(), feriados: feriadosSet,
          }),
        });
        setGravando(true);
        await programar({
          ensaioId: a.chip.ensaio_id, inicio: dataAlvo, frenteId: fr.id,
          motivo: 'Alocação no cronograma de máquinas.', origem: 'gantt',
        });
        setDesfazer(null);
        setOk(`${rotuloDe(a.chip)} alocado na frente ${fr.codigo} — começa em ${fmtData(dataAlvo)}.`);
        await carregar();
        return;
      }

      if (a.tipo === 'bandeja' && a.chip) {
        if (!alvo) return;
        if (a.chip.classe === 'trivial') {
          setRecado(`${a.chip.metodo_codigo} é ensaio trivial: vai para a frente `
            + `${a.chip.frente_sugerida_codigo ?? 'de serviço'}, não para uma máquina.`);
          return;
        }
        /*
         * A máquina TEM de servir para o grupo do método.
         *
         * `categorias_aceitas` vem da ponte grupo → categoria, que é
         * cadastro. Vazia quer dizer «ainda não declarada» — e aí a
         * tela não impede, porque impedir com base numa ligação que
         * ninguém preencheu travaria a programação em vez do erro.
         */
        const maq = dados?.maquinas.find((m) => m.equipamento_id === alvo);
        const aceita = a.chip.categorias_aceitas;
        if (maq && !serve(maq, a.chip)) {
          setRecado(
            `${a.chip.metodo_codigo} é do grupo «${a.chip.grupo ?? '—'}», `
            + `que roda em ${(aceita ?? []).join(' ou ')} — e `
            + `${maq.codigo_patrimonial} é ${maq.categoria}. Se esta `
            + 'máquina realmente faz esse ensaio, é cadastro: a exceção '
            + 'por máquina (grupos extras) pode ser declarada.');
          return;
        }
        /*
         * A data de quem cai da bandeja é a COLUNA em que caiu — e não
         * «hoje». Arrastar para a quinta e ver a barra pousar na
         * segunda faria o gesto mentir.
         */
        /*
         * Onda 200: fora das colunas (o chip solto sobre o NOME da
         * máquina), vale a data prevista do ensaio — e ela chega da API
         * como carimbo de hora, não como `AAAA-MM-DD`. Mandá-la inteira
         * era o «Dados inválidos» que fazia o arrasto parecer quebrado.
         */
        /*
         * 0195 (RN-196): a data é AUTOMÁTICA — a coluna futura onde caiu,
         * levada ao próximo dia útil, ou o próximo início da máquina.
         * É a mesma conta da prévia que o ponteiro acabou de mostrar.
         */
        const coluna = colunaSob(e);
        const dataAlvo = inicioDoArrasto({
          coluna: coluna == null ? null : paraIso(inicio + coluna),
          hoje, proximoInicio: maq?.proximo_inicio ?? null, feriados: feriadosSet,
        }).inicio;
        setGravando(true);
        await programar({
          ensaioId: a.chip.ensaio_id, inicio: dataAlvo, equipamentoId: alvo,
          motivo: 'Alocação no cronograma de máquinas.', origem: 'gantt',
        });
        setDesfazer(null);
        setOk(`${rotuloDe(a.chip)} alocado — começa em ${fmtData(dataAlvo)}.`);
        await carregar();
        return;
      }

      if (a.tipo === 'duracao' && a.barra) {
        const novo = Math.max(0.5, num(a.barra.duracao_dias) + passos);
        if (novo === num(a.barra.duracao_dias)) return;
        lembrar(a.barra);
        setGravando(true);
        await programar({
          ensaioId: a.barra.ensaio_id, duracaoDias: novo, origem: 'gantt',
        });
        setOk(`Duração de ${rotuloDe(a.barra)}: ${dias(novo)}.`);
        await carregar();
        return;
      }

      if (a.tipo === 'inicio' && a.barra) {
        if (!passos) return;
        const fim = a.barra.fim_previsto ?? a.barra.inicio_previsto;
        const novoInicio = paraIso(emDias(a.barra.inicio_previsto) + passos);
        if (novoInicio > fim) { setRecado('O início não passa do fim.'); return; }
        setPedindoMotivo({ barra: a.barra, novoInicio, novoFim: fim, equipamentoId: null });
        setMotivo('');
        return;
      }

      if (a.tipo === 'mover' && a.barra) {
        /* 0197: barra de FRENTE não troca de máquina pelo arrasto. */
        const trocouMaquina = !!a.barra.equipamento_id && alvo && alvo !== a.barra.equipamento_id;
        if (!passos && !trocouMaquina) return;
        if (trocouMaquina) {
          /* 0198: a mesma checagem do chip — a máquina faz este ensaio? */
          const maq = dados?.maquinas.find((m) => m.equipamento_id === alvo);
          if (maq && !serve(maq, { categorias_aceitas: a.barra.categorias_aceitas, grupo: a.barra.grupo })) {
            setRecado(`${a.barra.metodo_codigo} é do grupo «${a.barra.grupo ?? '—'}», `
              + `que roda em ${(a.barra.categorias_aceitas ?? []).join(' ou ')} — e `
              + `${maq.codigo_patrimonial} é ${maq.categoria}.`);
            return;
          }
          if (maq && maq.apto === false) {
            setRecado(`${maq.codigo_patrimonial} está impedida: ${maq.impedimento ?? 'inapta'}.`);
            return;
          }
        }
        if (passos) {
          /*
           * Mudar a data exige motivo (§8, «registrar o motivo dos
           * atrasos»). Trocar só de máquina não — a data continua a
           * mesma, e a revisão já grava quem trocou.
           */
          setPedindoMotivo({
            barra: a.barra,
            novoInicio: paraIso(emDias(a.barra.inicio_previsto) + passos),
            equipamentoId: trocouMaquina ? alvo : null,
          });
          setMotivo('');
          return;
        }
        lembrar(a.barra);
        setGravando(true);
        await programar({
          ensaioId: a.barra.ensaio_id, equipamentoId: alvo, origem: 'gantt',
        });
        setOk(`${rotuloDe(a.barra)} movido para ${dados?.maquinas.find((m) => m.equipamento_id === alvo)?.codigo_patrimonial ?? 'outra máquina'}.`);
        await carregar();
      }
    } catch (erroGravar) {
      setDesfazer(null);
      setRecado(mensagemDoErro(erroGravar));
      /* Um erro de gravação restaura a representação (§7): recarregar
         devolve a barra ao que o servidor tem. */
      await carregar();
    } finally {
      setGravando(false);
    }
  };

  const confirmarMotivo = async () => {
    if (!pedindoMotivo) return;
    try {
      lembrar(pedindoMotivo.barra);
      setGravando(true);
      await programar({
        ensaioId: pedindoMotivo.barra.ensaio_id,
        inicio: pedindoMotivo.novoInicio,
        fim: pedindoMotivo.novoFim,
        equipamentoId: pedindoMotivo.equipamentoId ?? undefined,
        motivo: motivo.trim(),
        origem: 'gantt',
      });
      setPedindoMotivo(null);
      setOk('Reprogramado.');
      await carregar();
    } catch (e) { setDesfazer(null); setRecado(mensagemDoErro(e)); }
    finally { setGravando(false); }
  };

  const fazerDesfazer = async () => {
    const d = desfazer;
    if (!d) return;
    setDesfazer(null);
    try {
      setGravando(true);
      await programar({
        ensaioId: d.ensaioId, inicio: d.inicio, duracaoDias: d.duracaoDias,
        equipamentoId: d.equipamentoId, correNoFimDeSemana: d.fds,
        motivo: 'Desfazer a última alteração no Gantt.', origem: 'gantt',
      });
      setOk(`${d.rotulo}: alteração desfeita.`);
      await carregar();
    } catch (e) { setRecado(mensagemDoErro(e)); }
    finally { setGravando(false); }
  };

  /* 0198: desfaz o lote da resolução de conflitos — os mesmos movimentos, invertidos. */
  const desfazerResolucao = async () => {
    const lote = desfazerLote;
    if (!lote) return;
    setDesfazerLote(null);
    try {
      setGravando(true);
      const r = await aplicarConflitos({
        movimentos: lote.map((m) => ({ ensaioId: m.ensaioId, inicioAtual: m.inicioNovo, inicioNovo: m.inicioAnterior })),
        motivo: 'Desfazer a resolução de conflitos.',
      });
      setOk(`${r.movidos} ensaio(s) de volta ao lugar.`);
      await carregar();
    } catch (e) { setRecado(mensagemDoErro(e)); }
    finally { setGravando(false); }
  };

  const desalocar = async (b: Barra) => {
    try {
      await alocar({ ensaioId: b.ensaio_id, equipamentoId: null, origem: 'formulario' });
      setOlhando(null);
      setOk('Ensaio devolvido para a bandeja.');
      await carregar();
    } catch (e) { setRecado(mensagemDoErro(e)); }
  };

  const alternar = (id: string) => setRecolhidas((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  /*
   * 0197: a linha de UM ensaio (a barra), a mesma para máquina e para
   * frente — o recurso só muda o `data-*` da linha, que é o alvo do
   * arrasto. Vivia dentro do laço das máquinas; as frentes a usam também.
   */
  const linhaDaBarra = (b: Barra, recurso: { maquina?: string; frente?: string; variosMetodos?: boolean }) => {
    const arr = arrasto?.ensaioId === b.ensaio_id ? arrasto : null;
    const dxMover = arr?.tipo === 'mover' ? arr.dx : 0;
    const dxDur = arr?.tipo === 'duracao' ? arr.dx : 0;
    const dxIni = arr?.tipo === 'inicio' ? arr.dx : 0;
    const fimP = b.fim_previsto ?? b.inicio_previsto;
    const esq = x(b.inicio_previsto) + dxMover + dxIni;
    const larg = Math.max(LARGURA_DIA - 4,
      (emDias(fimP) - emDias(b.inicio_previsto) + 1) * LARGURA_DIA
      - 4 + dxDur - dxIni);
    /* A régua da referência inicial: só quando a barra saiu de lá. */
    const base = b.remanejado && b.inicio_base ? (
      <div className="gm-base"
        style={{
          left: x(b.inicio_base),
          width: Math.max(LARGURA_DIA - 4,
            (emDias(b.fim_base ?? b.inicio_base)
             - emDias(b.inicio_base) + 1) * LARGURA_DIA - 4),
          top: alturaLinha - (compacta ? 5 : 6),
        }}
        title={`referência inicial: ${fmtData(b.inicio_base)}`
          + (b.fim_base ? ` a ${fmtData(b.fim_base)}` : '')} />
    ) : null;
    /*
     * A camada do REAL: do primeiro dado bruto até a
     * ficha completa — ou até hoje, aberta, enquanto
     * corre. Não se arrasta.
     */
    const real = b.inicio_real ? (() => {
      const fimR = b.fim_real ?? hoje;
      const aberta = !b.fim_real;
      return (
        <>
          <div className={`gm-real${aberta ? ' aberta' : ''}`}
            style={{
              left: x(b.inicio_real!),
              width: Math.max(6,
                (emDias(fimR) - emDias(b.inicio_real!) + 1) * LARGURA_DIA - 4),
              top: alturaLinha - (compacta ? 9 : 13),
            }}
            title={`realizado: ${fmtData(b.inicio_real!)} → `
              + (b.fim_real ? fmtData(b.fim_real) : 'em execução')}>
            <span className="gm-real-txt">
              {aberta ? 'em execução' : 'realizado'}
            </span>
          </div>
          {aberta && b.previsao_fim && emDias(b.previsao_fim) > emDias(hoje) && (
            <div className="gm-previsao"
              style={{
                left: x(hoje) + LARGURA_DIA,
                width: Math.max(4,
                  (emDias(b.previsao_fim) - emDias(hoje)) * LARGURA_DIA - 4),
                top: alturaLinha - (compacta ? 9 : 13),
              }}
              title={`previsão de término: ${fmtData(b.previsao_fim)} `
                + '(a duração programada contada do início real)'} />
          )}
        </>
      );
    })() : null;

    return (
      <div key={b.ensaio_id} className="gm-linha gm-ens"
        style={{ height: alturaLinha }}
        data-maquina={recurso.maquina} data-frente={recurso.frente}>
        <div className="gm-esq gm-esq-ens" style={{ paddingLeft: recuoDoNivel('amostra') }}>
          {/* 378 (spec 15 §3.3): a linha da amostra diz a nomenclatura do
              cliente · o cliente. A TAG do método abre a linha só na frente que
              reúne vários métodos; na máquina, ela vai na barra. O código AM e
              a OS ficam na dica e no painel do ensaio. */}
          <button type="button" className="gm-nome link-linha"
            title={`${b.metodo_codigo} — ${b.metodo_nome} · ${nomeDaAmostra(daApi(b, true))}`
              + `\n${b.cliente ?? 'sem cliente'}${b.obra_nome ? ` · ${b.obra_nome}` : ''}`}
            onClick={() => setOlhando(b)}>
            {rotuloDaLinhaDaAmostra({
              amostra_identificacao: b.identificacao_recebida, amostra_codigo: b.amostra_codigo,
              cliente_curto: clienteCurto(b.cliente), metodo_codigo: b.metodo_codigo,
            }, !!recurso.variosMetodos)}
          </button>
        </div>
        <div className="gm-faixa" style={{ width: colunas * LARGURA_DIA }}>
          <Fundo colunas={colunas} inicio={inicio} hojeDias={hojeDias} largura={LARGURA_DIA} feriados={feriadosSet} colunaAlvo={colunaAlvo} />
          {base}
          {real}
          <div
            data-ensaio={b.ensaio_id}
            className={['gm-barra',
              b.escopo ? `escopo-${b.escopo}` : '',
              b.em_conflito ? 'conflito' : '',
              b.atrasado || b.atraso_atual_dias > 0 ? 'atrasado' : '',
              b.critico && !soCritico ? 'critico' : '',
              b.remanejado ? 'remanejado' : '',
              b.fim_real ? 'concluido' : '',
              olhando?.ensaio_id === b.ensaio_id ? 'selecionada' : '',
              arr ? 'arrastando' : ''].filter(Boolean)
              .join(' ')}
            style={{
              left: esq, width: larg, top: compacta ? 3 : 4,
              cursor: podeProgramar ? 'grab' : 'pointer',
              /* 0198: a barra segue o ponteiro também na vertical, para
                 cair na linha de outra máquina. */
              transform: arr?.tipo === 'mover' && b.equipamento_id ? `translateY(${arr.dy}px)` : undefined,
              zIndex: arr ? 6 : undefined,
            }}
            title={`${rotuloDe(b)} · ${dias(b.duracao_dias)} (${b.duracao_unidade})`
              + `\nprogramado: ${fmtData(b.inicio_previsto)} → ${b.fim_previsto ? fmtData(b.fim_previsto) : '—'}`
              + (b.inicio_real ? `\nrealizado: ${fmtData(b.inicio_real)} → ${b.fim_real ? fmtData(b.fim_real) : 'em execução'}` : '')
              + (b.folga_dias == null
                ? '\nOS sem prazo contratual — sem folga a calcular'
                : `\nfolga de ${b.folga_dias} dia(s) contra o prazo`
                  + (b.critico
                    ? ' — no caminho crítico: atrasar um dia atrasa a entrega'
                    : ''))
              + (b.revisao_atual > 0 ? `\nrevisão ${b.revisao_atual} do programado` : '')}
            onPointerDown={(e) => {
              /* 378: só o botão esquerdo pega a barra; o meio e o espaço movem a tela. */
              if (!podeProgramar || e.button !== 0 || espaco.current) return;
              const t = e.target as HTMLElement;
              if (t.classList.contains('gm-alca')
                  || t.classList.contains('gm-alca-esq')) return;
              arrastou.current = false;
              setArrasto({
                tipo: 'mover', ensaioId: b.ensaio_id,
                origemX: e.clientX, origemY: e.clientY,
                dx: 0, dy: 0, barra: b,
              });
            }}
            onClick={() => {
              /* 0197: depois de um arrasto, o clique não abre o painel. */
              if (arrastou.current) { arrastou.current = false; return; }
              if (!arrasto) setOlhando(b);
            }}
          >
            <span className="gm-avanco" style={{ width: `${b.percentual}%` }} />
            {podeProgramar && (
              <span className="gm-alca-esq"
                title="Puxe para mudar o início mantendo o fim"
                onPointerDown={(e) => {
                  if (e.button !== 0 || espaco.current) return;
                  e.stopPropagation();
                  setArrasto({
                    tipo: 'inicio', ensaioId: b.ensaio_id,
                    origemX: e.clientX, origemY: e.clientY,
                    dx: 0, dy: 0, barra: b,
                  });
                }} />
            )}
            <span className="gm-txt">
              {/* Nas máquinas especiais, a TAG do método fica na barra (spec 15 §3.3). */}
              {recurso.maquina && <b className="gm-tag">{b.metodo_codigo} · </b>}
              {b.percentual}%
              {arr && arr.tipo !== 'bandeja' && (
                <small className="gm-arrasto-info">
                  {arr.tipo === 'duracao'
                    ? ` · ${dias(Math.max(0.5, num(b.duracao_dias) + Math.round(arr.dx / LARGURA_DIA)))}`
                    : ` · ${fmtData(paraIso(emDias(b.inicio_previsto) + Math.round(arr.dx / LARGURA_DIA)))}`}
                </small>
              )}
              {b.folga_dias != null && !arr && (
                <small className={b.critico ? 'gm-folga critica' : 'gm-folga'}>
                  {' · '}{b.folga_dias >= 0 ? '+' : ''}{b.folga_dias}d
                </small>
              )}
            </span>
            {podeProgramar && (
              <span className="gm-alca"
                title="Puxe para mudar a duração, em dias"
                onPointerDown={(e) => {
                  if (e.button !== 0 || espaco.current) return;
                  e.stopPropagation();
                  setArrasto({
                    tipo: 'duracao', ensaioId: b.ensaio_id,
                    origemX: e.clientX, origemY: e.clientY,
                    dx: 0, dy: 0, barra: b,
                  });
                }} />
            )}
          </div>
        </div>
      </div>
    );
  };

  if (erro) return <ErroDaApi erro={erro} />;
  if (!dados) return <Carregando texto="Abrindo a linha do tempo das máquinas" />;

  const podeProgramar = pode('os.programar');
  const c = dados.contadores;
  /* 378: «Recolher tudo» recolhe os equipamentos E as frentes (spec 15 §3.3). */
  const chavesDosRecursos = [
    ...dados.maquinas.map((m) => chaveDeRecolher('equipamento', m.equipamento_id)),
    ...dados.frentes.map((f) => chaveDeRecolher('frente', f.id)),
  ];
  const todasRecolhidas = chavesDosRecursos.length > 0
    && chavesDosRecursos.every((k) => recolhidas.has(k));

  /*
   * 266 · os contadores filtram. Contam ENSAIOS no filtro e em qualquer
   * data (a API diz a unidade); o clique mostra na janela só as barras
   * daquela situação. 378: «Sem agendamento» FILTRA — a grade fica sem os
   * agendados e a bandeja (que é onde eles estão) abre; «Todos» limpa.
   */
  const passa = (b: Barra) => {
    if (!situacao) return true;
    if (situacao === 'sem_agendamento') return false;
    if (situacao === 'em_execucao') return !!b.inicio_real && !b.fim_real;
    if (situacao === 'concluidos') return !!b.fim_real;
    if (situacao === 'atrasados') return !!b.atrasado || b.atraso_atual_dias > 0;
    return !b.inicio_real;
  };
  const CONTADORES: { chave: '' | typeof situacao; rotulo: string; n: number; tom: string }[] = [
    { chave: '', rotulo: 'Todos', n: c.sem_agendamento + c.agendados + c.em_execucao + c.concluidos, tom: 'todos' },
    { chave: 'sem_agendamento', rotulo: 'Sem agendamento', n: c.sem_agendamento, tom: 'sem' },
    { chave: 'agendados', rotulo: 'Agendados', n: c.agendados, tom: 'agendado' },
    { chave: 'em_execucao', rotulo: 'Em execução', n: c.em_execucao, tom: 'andamento' },
    { chave: 'concluidos', rotulo: 'Concluídos', n: c.concluidos, tom: 'ok' },
    { chave: 'atrasados', rotulo: 'Atrasados', n: c.atrasados, tom: c.atrasados ? 'critico' : 'atrasado' },
  ];

  /* Os filtros que valem agora, como etiquetas que se tiram com um toque. */
  const ativos: { rotulo: string; tirar(): void }[] = [];
  if (classe) ativos.push({ rotulo: classe === 'especial' ? 'Só especiais' : 'Só triviais', tirar: () => setClasse('') });
  if (metodo) ativos.push({ rotulo: `Ensaio: ${dados.metodos.find((m) => m.id === metodo)?.codigo ?? '?'}`, tirar: () => setMetodo('') });
  if (os) ativos.push({ rotulo: `OS ${dados.oss.find((o) => o.id === os)?.numero ?? '?'}`, tirar: () => setOs('') });
  if (cliente) ativos.push({ rotulo: `Cliente: ${clienteCurto(dados.clientes.find((cl) => cl.id === cliente)?.nome)}`, tirar: () => setCliente('') });

  /*
   * 266 · a bandeja agrupada (por OS ou por método) e procurável. A busca
   * daqui é local — só a lista da bandeja; a «Procurar» da barra continua
   * filtrando a tela inteira no servidor.
   */
  const termo = buscaBandeja.trim().toLowerCase();
  const naBandeja = dados.bandeja.filter((ch) => !termo || [
    ch.metodo_codigo, ch.amostra_codigo, ch.identificacao_recebida ?? '',
    ch.os_numero ?? '', ch.cliente ?? '',
  ].some((v) => v.toLowerCase().includes(termo)));
  const gruposDaBandeja: { chave: string; titulo: string; det: string; chips: NaBandeja[] }[] = [];
  for (const ch of naBandeja) {
    const chave = agrupar === 'os' ? (ch.os_numero ?? 'sem OS') : ch.metodo_codigo;
    let g = gruposDaBandeja.find((x) => x.chave === chave);
    if (!g) {
      g = {
        chave,
        titulo: agrupar === 'os' ? (ch.os_numero ? `OS ${ch.os_numero}` : 'Sem OS') : ch.metodo_codigo,
        det: agrupar === 'os' ? clienteCurto(ch.cliente) : ch.metodo_nome,
        chips: [],
      };
      gruposDaBandeja.push(g);
    }
    g.chips.push(ch);
  }
  const marcadosChips = dados.bandeja.filter((ch) => marcados.has(ch.ensaio_id));
  const alternarMarcado = (id: string) => setMarcados((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const alternarGrupoBandeja = (chave: string) => setGruposFechados((s) => {
    const n = new Set(s);
    if (n.has(chave)) n.delete(chave); else n.add(chave);
    return n;
  });
  const emConflito = dados.barras.filter((b) => b.em_conflito).length;
  const escala = escalaDoCabecalho(LARGURA_DIA);

  /*
   * 378 · as linhas que a grade mostra. O filtro de situação manda na
   * contagem do equipamento e da frente (spec 15 §3.3) e, com «Ocultar
   * linhas vazias», tira o recurso sem nenhum ensaio no filtro.
   */
  const noFiltro = (lista: Barra[] | undefined) => (lista ?? []).filter(passa);
  const gruposVisiveis = grupos
    .map((g) => ({ ...g, maquinas: g.maquinas.filter((m) => !ocultarVazias || noFiltro(porMaquina.get(m.equipamento_id)).length > 0) }))
    .filter((g) => g.maquinas.length > 0);
  const familiasVisiveis = familias
    .map((f) => ({ ...f, frentes: f.frentes.filter((fr) => !ocultarVazias || noFiltro(porFrente.get(fr.id)).length > 0) }))
    .filter((f) => f.frentes.length > 0);

  /* 378 · as ações raras moram no ⋯ (spec 15 §3.2). */
  const mais: AcaoDoMais[] = [
    {
      rotulo: todasRecolhidas ? 'Expandir tudo' : 'Recolher tudo', classe: 'gm-recolher-tudo',
      acao: () => setRecolhidas(todasRecolhidas ? new Set() : new Set(chavesDosRecursos)),
    },
    ...(podeProgramar ? [{
      rotulo: 'Durações padrão', classe: 'gm-duracoes-botao',
      acao: () => { void verDuracoes().then(setDuracoes).catch((e) => setRecado(mensagemDoErro(e))); },
    }] : []),
    ...(podeProgramar && dados.frentes.length > 0 ? [{
      rotulo: 'Capacidade das frentes', classe: 'gm-capacidades-botao', acao: () => setCapacidades(true),
    }] : []),
  ];

  /* «Ajustar»: enquadra o ensaio aberto, senão o que o filtro mostra (a OS filtrada). */
  const fazerAjustar = () => {
    const alvo = olhando ? [olhando] : dados.barras.filter(passa);
    if (alvo.length === 0) { setRecado('Nada a enquadrar: nenhum ensaio agendado no filtro.'); return; }
    const primeiro = Math.min(...alvo.map((b) => emDias(b.inicio_previsto)));
    const ultimo = Math.max(...alvo.map((b) => emDias(b.fim_previsto ?? b.inicio_previsto)));
    const a = ajustar(primeiro, ultimo, larguraVisivel || 800);
    larguraRef.current = a.larguraDia;
    rolagemPendente.current = 0;
    setLarguraDia(a.larguraDia);
    setDe(paraIso(a.de));
  };

  const legenda = (
    <>
      <ul className="gm-legenda" aria-label="Legenda">
        <li><i className="gm-leg prog" /> programado (a barra se arrasta)</li>
        <li><i className="gm-leg real" /> realizado — do primeiro dado bruto à ficha completa</li>
        <li><i className="gm-leg aberta" /> em execução — a faixa real vai até hoje</li>
        <li><i className="gm-leg prev" /> previsão de término (duração programada contada do início real)</li>
        <li><i className="gm-leg base" /> referência inicial, quando a barra saiu de lá</li>
        <li><i className="gm-leg critico" /> caminho crítico · <i className="gm-leg conflito" /> conflito de postos</li>
        <li><i className="gm-leg carga" /> carga da frente no dia · <i className="gm-leg carga lotada" /> acima da capacidade</li>
        <li><i className="gm-leg hoje" /> hoje · <i className="gm-leg fds" /> sábado, domingo e feriado de Blumenau</li>
      </ul>
      <p className="rodape-regra">
        A barra é a <b>ocupação da máquina</b> (nas frentes, o dia do ensaio
        trivial, contado na capacidade). Arraste a barra de lado para mudar o
        início (o sistema pede o motivo), para cima ou para baixo para trocar de
        máquina; a alça da direita muda a duração e a da esquerda muda o início
        mantendo o fim. A faixa fina é o realizado: do primeiro dado bruto à
        ficha completa — não à aprovação —, e nunca move o programado. Sábado,
        domingo e feriado de Blumenau não contam na duração, salvo quando o
        ensaio corre no fim de semana. Cada mudança do programado vira uma
        revisão numerada, listada no painel do ensaio.
      </p>
      <p className="rodape-regra">
        Rodinha: rola as linhas · Shift + rodinha: os dias · Ctrl + rodinha,
        pinça ou + −: zoom no ponto do cursor · botão do meio ou espaço +
        arrastar: move a tela · F: tela cheia (Esc sai).
      </p>
    </>
  );

  const filtros = (
    /* Sempre no DOM, escondido quando fechado: os campos continuam
       existindo para o teclado e para quem os procura pelo rótulo. */
    <div className="filtros gm-filtros" id="gm-filtros" hidden={!filtrosAbertos}>
      <label className="campo">
        <span>Ensaios</span>
        <select value={classe} aria-label="Classe de ensaio"
          onChange={(e) => setClasse(e.target.value as '' | 'especial' | 'trivial')}>
          <option value="">todos</option>
          <option value="especial">só especiais (máquinas)</option>
          <option value="trivial">só triviais (frentes)</option>
        </select>
      </label>
      <label className="campo">
        <span>Tipo de ensaio</span>
        <select value={metodo} onChange={(e) => setMetodo(e.target.value)}>
          <option value="">todos</option>
          {dados.metodos.filter((m) => !classe || m.classe === classe).map((m) => (
            <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span>Ordem de serviço</span>
        <select value={os} onChange={(e) => setOs(e.target.value)}>
          <option value="">todas</option>
          {dados.oss.map((o) => (
            <option key={o.id} value={o.id}>
              {o.numero ?? '—'}{o.cliente ? ` · ${o.cliente}` : ''}
              {o.prazo_contratual
                ? ` · até ${fmtData(o.prazo_contratual)}` : ' · sem prazo'}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span>Cliente</span>
        <select value={cliente} onChange={(e) => setCliente(e.target.value)}>
          <option value="">todos</option>
          {dados.clientes.map((cl) => (
            <option key={cl.id} value={cl.id}>{cl.nome}</option>
          ))}
        </select>
      </label>
      <button type="button" className="botao discreto"
        onClick={() => setFiltrosAbertos(false)}>Fechar</button>
    </div>
  );

  /* O cabeçalho troca dias → semanas → meses conforme o zoom (`escalaDoCabecalho`). */
  const faixasDoCabecalho = (() => {
    const meses: { chave: string; rotulo: string; curto: string; ano: number; dias: number }[] = [];
    const semanas: { chave: string; rotulo: string; dias: number }[] = [];
    const anos: { ano: number; dias: number }[] = [];
    for (let i = 0; i < colunas; i += 1) {
      const dt = new Date((inicio + i) * DIA_MS);
      const mes = dt.getUTCMonth(); const ano = dt.getUTCFullYear();
      const chave = `${ano}-${mes}`;
      const um = meses[meses.length - 1];
      if (um && um.chave === chave) um.dias += 1;
      else meses.push({ chave, rotulo: `${MESES[mes]} ${ano}`, curto: MESES[mes].slice(0, 3), ano, dias: 1 });
      const sem = semanas[semanas.length - 1];
      if (sem && dt.getUTCDay() !== 1) sem.dias += 1;
      else semanas.push({ chave: paraIso(inicio + i), rotulo: `${String(dt.getUTCDate()).padStart(2, '0')}/${String(mes + 1).padStart(2, '0')}`, dias: 1 });
      const a = anos[anos.length - 1];
      if (a && a.ano === ano) a.dias += 1; else anos.push({ ano, dias: 1 });
    }
    return { meses, semanas, anos };
  })();

  const cabecalho = () => {
    const { meses: ms, semanas, anos } = faixasDoCabecalho;
    if (escala === 'mes') {
      return (
        <>
          <div className="gm-meses gm-anos">
            {anos.map((a) => (
              <div key={a.ano} className="gm-mes" style={{ width: a.dias * LARGURA_DIA }}>
                {a.dias * LARGURA_DIA > 40 ? a.ano : ''}
              </div>
            ))}
          </div>
          <div className="gm-dias gm-meses-curtos">
            {ms.map((m) => (
              <div key={m.chave} className="gm-dia gm-mes-curto" title={m.rotulo} style={{ width: m.dias * LARGURA_DIA }}>
                <b>{m.dias * LARGURA_DIA > 26 ? m.curto : ''}</b>
              </div>
            ))}
          </div>
        </>
      );
    }
    return (
      <>
        <div className="gm-meses">
          {ms.map((m) => (
            <div key={m.chave} className="gm-mes" style={{ width: m.dias * LARGURA_DIA }}>
              {m.dias * LARGURA_DIA > 70 ? m.rotulo : ''}
            </div>
          ))}
        </div>
        {escala === 'semana' ? (
          <div className="gm-dias gm-semanas">
            {semanas.map((w) => (
              <div key={w.chave} className="gm-dia gm-semana" title={`semana de ${fmtData(w.chave)}`}
                style={{ width: w.dias * LARGURA_DIA }}>
                <b>{w.dias * LARGURA_DIA > 34 ? w.rotulo : ''}</b>
              </div>
            ))}
          </div>
        ) : (
          <div className="gm-dias">
            {Array.from({ length: colunas }, (_, i) => {
              const d = inicio + i;
              const dt = new Date(d * DIA_MS);
              const dow = dt.getUTCDay();
              const iso = paraIso(d);
              const feriado = feriados.has(iso);
              return (
                <div key={i}
                  className={['gm-dia',
                    dow === 0 || dow === 6 ? 'fds' : '',
                    feriado ? 'feriado' : '',
                    d === hojeDias ? 'hoje' : '',
                    colunaAlvo === i ? 'alvo' : ''].filter(Boolean).join(' ')}
                  title={feriado ? `Feriado: ${feriados.get(iso) ?? ''}` : undefined}
                  style={{ width: LARGURA_DIA }}>
                  <b>{dt.getUTCDate()}</b>
                  {LARGURA_DIA >= 24 && !compacta && <small>{feriado ? 'fer.' : DIA_SEMANA[dow]}</small>}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  /** O ▾ do recolher: dentro do recuo do nível (antes do nome na tela, depois dele no DOM). */
  const botaoDeRecolher = (chave: string, nome: string) => {
    const fechado = recolhidas.has(chave);
    return (
      <button type="button" className="gm-recolhe"
        aria-expanded={!fechado}
        aria-label={`${fechado ? 'Expandir' : 'Recolher'} ${nome}`}
        onClick={() => alternar(chave)}>
        {fechado ? '▸' : '▾'}
      </button>
    );
  };

  return (
    <div className={['programacao-maquinas gm-a', compacta ? 'gm-compacta' : '', telaCheia ? 'gm-em-tela-cheia' : '']
      .filter(Boolean).join(' ')}>
      {/* 378: o título e o subtítulo saem da vista (a trilha já diz onde se
          está; a altura vai para a grade) e ficam para o leitor de tela. */}
      <h1 className="so-leitor">Cronograma</h1>
      <p className="so-leitor">
        Ensaios especiais por equipamento e triviais por frente · {janela} dias a partir de {fmtData(de)}
        {' '}· a barra é o programado (a ocupação da máquina), a faixa fina embaixo é o realizado
        {' '}· sábado, domingo e feriado de Blumenau não contam na duração
      </p>

      <BarraDoCronograma
        telaCheia={telaCheia} aoTelaCheia={() => setTelaCheia((v) => !v)}
        aoHoje={() => { setDe(hoje); if (corpo.current) corpo.current.scrollLeft = 0; }}
        aoAnterior={() => setDe(paraIso(inicio - janela))}
        aoProximo={() => setDe(paraIso(inicio + janela))}
        de={de} aoDe={(v) => setDe(v || hoje)}
        janela={janela} aoJanela={setJanela}
        larguraDia={LARGURA_DIA}
        aoEscala={(e) => aplicarZoom(ATALHO_DA_ESCALA[e] / larguraRef.current)}
        aoZoom={(f) => aplicarZoom(f)}
        aoRegua={(l) => aplicarZoom(l / larguraRef.current)}
        aoAjustar={fazerAjustar}
        filtrosAtivos={ativos.length} filtrosAbertos={filtrosAbertos}
        aoFiltros={() => setFiltrosAbertos((v) => !v)} filtros={filtros}
        busca={busca} aoBusca={setBusca}
        conflitos={podeProgramar ? emConflito : null} aoConflitos={() => setResolvendo({})}
        legendaAberta={legendaAberta} aoLegenda={() => setLegendaAberta((v) => !v)} legenda={legenda}
        mais={mais} maisAberto={maisAberto} aoMais={setMaisAberto}
        podeAgendar={podeProgramar}
        aoAgendar={() => setNovaProg({
          ensaioId: dados.bandeja[0]?.ensaio_id ?? '',
          equipamentoId: '', frenteId: '', data: hoje, duracao: '', fds: false,
          obs: '',
        })} />

      {recado && <Aviso tipo="erro">{recado}</Aviso>}
      {ok && (
        <Aviso tipo="ok">
          {ok}
          {desfazerLote && (
            <>
              {' '}
              <button type="button" className="botao discreto gm-desfazer"
                disabled={gravando}
                onClick={() => { void desfazerResolucao(); }}>
                Desfazer o lote
              </button>
            </>
          )}
          {desfazer && (
            <>
              {' '}
              <button type="button" className="botao discreto gm-desfazer"
                disabled={gravando} onClick={() => { void fazerDesfazer(); }}>
                Desfazer
              </button>
            </>
          )}
        </Aviso>
      )}

      {/*
        * 378 · a linha de filtros com contagem, no lugar dos cartões: os
        * mesmos números de sempre — ENSAIOS, no filtro e em qualquer data —
        * como botões `aria-pressed` que filtram de verdade; as etiquetas dos
        * filtros ativos; e as duas chaves.
        */}
      <div className="gm-cartoes" role="group"
        aria-label={`Filtrar pela situação — ${c.unidade}, no filtro, em qualquer data`}>
        {CONTADORES.map((k) => {
          const ativo = situacao === k.chave;
          return (
            <button key={k.rotulo} type="button"
              className={`gm-cartao ${k.tom}${ativo ? ' ativo' : ''}`}
              aria-pressed={ativo}
              onClick={() => {
                if (k.chave === 'sem_agendamento') setBandejaAberta(true);
                setSituacao((s) => (s === k.chave ? '' : k.chave));
              }}>
              <i className="gm-cartao-ic" aria-hidden="true" />
              <small>{k.rotulo}</small>
              <strong>{k.n}</strong>
            </button>
          );
        })}
        {ativos.map((a) => (
          <span key={a.rotulo} className="gm-etiqueta">
            {a.rotulo}
            <button type="button" aria-label={`Tirar o filtro ${a.rotulo}`} onClick={a.tirar}>×</button>
          </span>
        ))}
        <span className="cresce" />
        <label className="gm-alterna">
          <input type="checkbox" checked={soCritico}
            onChange={(e) => setSoCritico(e.target.checked)} />
          <span className="gm-alterna-trilho" aria-hidden="true" />
          Só o caminho crítico
        </label>
        <label className="gm-alterna">
          <input type="checkbox" checked={ocultarVazias}
            onChange={(e) => setOcultarVazias(e.target.checked)} />
          <span className="gm-alterna-trilho" aria-hidden="true" />
          Ocultar linhas vazias
        </label>
      </div>

      <div ref={area} className={`gm-area${bandejaAberta ? '' : ' sem-bandeja'}`}>
        <div className="gm-palco">
          {dados.maquinas.length === 0 && dados.frentes.length === 0 ? (
            <Vazio>
              Nenhuma máquina cadastrada nas famílias de ensaio especial
              (prensa de adensamento, cisalhamento direto, triaxial,
              multiprensa, prensa CBR). Cadastre o equipamento e ele aparece
              como uma faixa aqui.
            </Vazio>
          ) : (
            <div className={`gantt-maquinas gm-lista${movendo ? ' movendo' : ''}`} ref={corpo} onScroll={medirSetas}
              data-de={dados.de ?? undefined} data-largura-dia={LARGURA_DIA} data-escala={escala}
              onPointerDown={comecarAMover} onPointerMove={mover}
              onPointerUp={pararDeMover} onPointerCancel={pararDeMover}
              onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
              onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}
              style={{ ['--gm-dia' as string]: `${LARGURA_DIA}px` }}>
              {setas.length > 0 && (
                <svg className="gm-setas" aria-hidden="true">
                  <defs>
                    <marker id="gm-ponta" markerWidth="7" markerHeight="7"
                      refX="6" refY="3.5" orient="auto">
                      <path d="M0,0 L7,3.5 L0,7 z" />
                    </marker>
                  </defs>
                  {setas.map((a) => {
                    const meio = Math.max(a.x1 + 8, a.x2 - 8);
                    return (
                      <path key={`${a.de}-${a.para}`}
                        d={`M${a.x1},${a.y1} H${meio} V${a.y2} H${a.x2}`}
                        markerEnd="url(#gm-ponta)" />
                    );
                  })}
                </svg>
              )}

              {/* ── cabeçalho: mês e dia (ou semana, ou ano e mês) ─────────── */}
              <div className="gm-cabeca">
                <div className="gm-esq gm-esq-cab">
                  <div className="gm-nome">Equipamento · ensaio</div>
                </div>
                <div className="gm-regua" ref={regua} style={{ width: colunas * LARGURA_DIA }}>
                  {cabecalho()}
                </div>
              </div>

              {classe !== 'trivial' && gruposVisiveis.map((g) => {
                const chaveG = chaveDeRecolher('grupo', g.nome);
                const grupoFechado = recolhidas.has(chaveG);
                return (
                  <div key={g.nome} className="gm-grupo">
                    <div className="gm-grupo-titulo gm-esq-larga" style={{ paddingLeft: recuoDoNivel('grupo') }}>
                      <span className="gm-grupo-nome">{g.nome}</span>
                      {botaoDeRecolher(chaveG, g.nome)}
                      <small className="det">{plural(g.maquinas.length, 'equipamento', 'equipamentos')}</small>
                    </div>

                    {!grupoFechado && g.maquinas.map((mq) => {
                      const barras = porMaquina.get(mq.equipamento_id) ?? [];
                      const visiveis = barras.filter(passa);
                      const chave = chaveDeRecolher('equipamento', mq.equipamento_id);
                      const recolhida = recolhidas.has(chave);
                      return (
                        <div key={mq.equipamento_id}
                          className={`gm-maq${alvo === mq.equipamento_id
                            && !(arrasto?.tipo === 'mover' && arrasto.barra?.equipamento_id === mq.equipamento_id) ? ' gm-alvo' : ''}`}
                          data-maquina={mq.equipamento_id}>
                          {/* a linha da máquina — recolhível, e alvo de arrasto */}
                          <div className="gm-linha gm-maq-cab" style={{ height: alturaCab }}>
                            <div className="gm-esq" style={{ paddingLeft: recuoDoNivel('recurso') }}>
                              {/* 0191: só o código — a descrição repete o
                                  grupo; fica no title (decisão de 22/09). */}
                              <span className="gm-maq-cod" title={mq.nome}>{mq.codigo_patrimonial}</span>
                              {botaoDeRecolher(chave, mq.codigo_patrimonial)}
                              <small className="det gm-maq-info">
                                {plural(visiveis.length, 'ensaio', 'ensaios')}
                                {mq.postos > 1 && ` · ${mq.postos} postos`}
                                {podeProgramar && barras.some((b) => b.em_conflito) && (
                                  <>
                                    {' · '}
                                    <button type="button" className="link-linha gm-conflito-link"
                                      title="Resolver os conflitos desta máquina"
                                      onClick={(e) => { e.stopPropagation(); setResolvendo({ equipamentoId: mq.equipamento_id }); }}>
                                      {barras.filter((b) => b.em_conflito).length} em conflito · resolver
                                    </button>
                                  </>
                                )}
                                {' · '}
                                {mq.apto === false ? (
                                  <span className="erro-fila">{mq.impedimento ?? 'impedida'}</span>
                                ) : mq.livre_em === hoje ? 'livre hoje'
                                  : mq.livre_em ? `livre em ${fmtData(mq.livre_em)}`
                                    : 'sem previsão'}
                              </small>
                            </div>
                            <div className="gm-faixa" style={{ width: colunas * LARGURA_DIA }}>
                              <Fundo colunas={colunas} inicio={inicio} hojeDias={hojeDias} largura={LARGURA_DIA} feriados={feriadosSet} colunaAlvo={colunaAlvo} />
                              {/* 266 · a ocupação da máquina, num fio, na linha dela. */}
                              {barras.map((b) => (
                                <span key={b.ensaio_id} className="gm-ocupa" aria-hidden="true"
                                  style={{
                                    left: x(b.inicio_previsto),
                                    width: (emDias(b.fim_previsto ?? b.inicio_previsto) - emDias(b.inicio_previsto) + 1) * LARGURA_DIA,
                                  }} />
                              ))}
                              {/* 0195: a prévia — onde a barra vai pousar. */}
                              {previa && previa.maquina === mq.equipamento_id && arrasto?.chip && (
                                <div className={`gm-barra gm-previa${arrasto.chip.escopo ? ` escopo-${arrasto.chip.escopo}` : ''}`}
                                  data-inicio={previa.inicio} data-fim={previa.fim}
                                  style={{
                                    left: x(previa.inicio),
                                    width: Math.max(LARGURA_DIA - 4,
                                      (emDias(previa.fim) - emDias(previa.inicio) + 1) * LARGURA_DIA - 4),
                                  }}>
                                  <span>{fmtData(previa.inicio)} · {previa.dur} d</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {!recolhida && visiveis.map((b) => linhaDaBarra(b, { maquina: mq.equipamento_id }))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              {/*
                * 0197: as FRENTES — caracterização, compactação/CBR — abaixo
                * dos especiais, no mesmo Gantt («devem ser juntas no mesmo
                * gantt, abaixo dos ensaios especiais», 22/09). A frente não
                * tem posto: tem capacidade em ensaios por dia, e a faixa do
                * cabeçalho mostra a carga de cada dia — em vermelho quando
                * passa da capacidade.
                */}
              {classe !== 'especial' && familiasVisiveis.map((fam) => {
                const chaveG = chaveDeRecolher('grupo', `familia:${fam.familia}`);
                const grupoFechado = recolhidas.has(chaveG);
                return (
                  <div key={fam.familia} className="gm-grupo gm-frentes">
                    <div className="gm-grupo-titulo gm-esq-larga" style={{ paddingLeft: recuoDoNivel('grupo') }}>
                      <span className="gm-grupo-nome">{FAMILIA[fam.familia]}</span>
                      {botaoDeRecolher(chaveG, FAMILIA[fam.familia])}
                      <small className="det">{plural(fam.frentes.length, 'frente', 'frentes')} · ensaios triviais</small>
                    </div>

                    {!grupoFechado && fam.frentes.map((fr) => {
                      const barras = porFrente.get(fr.id) ?? [];
                      const visiveis = barras.filter(passa);
                      const chave = chaveDeRecolher('frente', fr.id);
                      const recolhida = recolhidas.has(chave);
                      const carga = cargas.get(fr.id) ?? new Map<string, number>();
                      const lotados = fr.carga.filter((cg) => cg.n > fr.capacidade_dia).length;
                      return (
                        <div key={fr.id}
                          className={`gm-maq gm-frente${alvoFrente === fr.id ? ' gm-alvo' : ''}`}
                          data-frente={fr.id}>
                          <div className="gm-linha gm-maq-cab" style={{ height: alturaCab }}>
                            <div className="gm-esq" style={{ paddingLeft: recuoDoNivel('recurso') }}>
                              <span className="gm-maq-cod" title={fr.nome}>{fr.codigo}</span>
                              {botaoDeRecolher(chave, fr.nome)}
                              <small className="det gm-maq-info">
                                {plural(visiveis.length, 'ensaio', 'ensaios')} · {fr.capacidade_dia}/dia
                                {lotados > 0 && <> · <span className="erro-fila">{plural(lotados, 'dia acima', 'dias acima')}</span></>}
                              </small>
                            </div>
                            <div className="gm-faixa" style={{ width: colunas * LARGURA_DIA }}>
                              <Fundo colunas={colunas} inicio={inicio} hojeDias={hojeDias} largura={LARGURA_DIA} feriados={feriadosSet} colunaAlvo={colunaAlvo} />
                              {/* a carga por dia, sobre o fundo */}
                              {Array.from({ length: colunas }, (_, i) => {
                                const iso = paraIso(inicio + i);
                                const n = carga.get(iso) ?? 0;
                                if (!n) return null;
                                const cheio = n > fr.capacidade_dia;
                                return (
                                  <span key={i}
                                    className={`gm-carga${cheio ? ' lotada' : ''}`}
                                    data-dia={iso} data-n={n}
                                    title={`${fmtData(iso)}: ${plural(n, 'ensaio', 'ensaios')} de ${fr.capacidade_dia}${cheio ? ' — acima da capacidade' : ''}`}
                                    style={{ left: i * LARGURA_DIA, width: LARGURA_DIA }}>
                                    {LARGURA_DIA >= 24 ? n : ''}
                                  </span>
                                );
                              })}
                              {previa && previa.frente === fr.id && arrasto?.chip && (
                                <div className={`gm-barra gm-previa${arrasto.chip.escopo ? ` escopo-${arrasto.chip.escopo}` : ''}`}
                                  data-inicio={previa.inicio} data-fim={previa.fim}
                                  style={{
                                    left: x(previa.inicio),
                                    width: Math.max(LARGURA_DIA - 4,
                                      (emDias(previa.fim) - emDias(previa.inicio) + 1) * LARGURA_DIA - 4),
                                  }}>
                                  <span>{fmtData(previa.inicio)} · {previa.dur} d</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {!recolhida && visiveis.map((b) => linhaDaBarra(b, {
                            frente: fr.id, variosMetodos: (fr.metodos?.length ?? 0) > 1,
                          }))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── o detalhe da barra (266): POR CIMA da grade, e a bandeja
              continua à direita — dá para editar um ensaio e seguir
              arrastando outros. ─────────────────────────────────────── */}
          {olhando && (
            <DetalheDaBarra
              key={olhando.ensaio_id}
              barra={olhando} maquinas={dados.maquinas}
              podeProgramar={podeProgramar}
              aoFechar={() => setOlhando(null)}
              aoDesalocar={() => { void desalocar(olhando); }}
              aoResolver={() => { setResolvendo({ ensaioId: olhando.ensaio_id }); setOlhando(null); }}
              aoGravar={async (corpoGravar) => {
                try {
                  lembrar(olhando);
                  setGravando(true);
                  await programar(corpoGravar);
                  setOk('Programação gravada.');
                  setOlhando(null);
                  await carregar();
                } catch (e) { setDesfazer(null); setRecado(mensagemDoErro(e)); }
                finally { setGravando(false); }
              }} />
          )}
        </div>

        {/*
          * A bandeja (266): painel à DIREITA da grade, sempre à vista — o
          * arrasto vai de lado, com a faixa de destino na mesma altura.
          * Agrupada por OS ou por método, procurável, e com caixas para
          * programar vários de uma vez. O chip continua o mesmo botão:
          * arrasta ou, num clique, abre o formulário.
          */}
        <aside className={`gm-bandeja${bandejaAberta ? '' : ' recolhida'}`} aria-label="Ensaios sem agendamento">
          <div className="gm-bandeja-cab">
            <h2 className="secao">Sem agendamento</h2>
            <b className="gm-conta">{dados.bandeja.length}</b>
            <button type="button" className="gm-recolhe gm-recolhe-bandeja"
              aria-expanded={bandejaAberta}
              aria-label={bandejaAberta ? 'Recolher a bandeja' : 'Abrir a bandeja'}
              title={bandejaAberta ? 'Recolher a bandeja' : 'Abrir a bandeja'}
              onClick={() => setBandejaAberta((v) => !v)}>
              {bandejaAberta ? '›' : '‹'}
            </button>
          </div>
          {bandejaAberta && (
            <>
              <div className="gm-bandeja-ferramentas">
                <label className="campo gm-procura">
                  <span className="so-leitor">Procurar na bandeja</span>
                  <input type="search" value={buscaBandeja}
                    onChange={(e) => setBuscaBandeja(e.target.value)}
                    placeholder="Amostra, OS ou método" />
                </label>
                <div className="gm-segmentos" role="group" aria-label="Agrupar a bandeja">
                  <button type="button" aria-pressed={agrupar === 'os'} onClick={() => setAgrupar('os')}>Por OS</button>
                  <button type="button" aria-pressed={agrupar === 'metodo'} onClick={() => setAgrupar('metodo')}>Por método</button>
                </div>
              </div>
              <div className="gm-chips">
                {naBandeja.length === 0 && (
                  <p className="det gm-bandeja-vazia">
                    {dados.bandeja.length === 0 ? 'Nenhum ensaio sem agendamento no filtro.' : 'Nada com esse termo.'}
                  </p>
                )}
                {gruposDaBandeja.map((g) => {
                  const fechado = gruposFechados.has(g.chave);
                  return (
                    <div key={g.chave} className="gm-bandeja-grupo">
                      <button type="button" className="gm-bandeja-grupo-cab"
                        aria-expanded={!fechado} onClick={() => alternarGrupoBandeja(g.chave)}>
                        <span aria-hidden="true">{fechado ? '▸' : '▾'}</span>
                        <b>{g.titulo}</b>
                        <span className="det">{g.det}</span>
                        <em>{g.chips.length}</em>
                      </button>
                      {!fechado && g.chips.map((ch) => (
                        <div key={ch.ensaio_id} className={`gm-chip-linha${marcados.has(ch.ensaio_id) ? ' marcado' : ''}`}>
                          {podeProgramar && (
                            <input type="checkbox" checked={marcados.has(ch.ensaio_id)}
                              aria-label={`Marcar ${rotuloDe(ch)}`}
                              onChange={() => alternarMarcado(ch.ensaio_id)} />
                          )}
                          <button
                            type="button"
                            className={['gm-chip', ch.escopo ? `escopo-${ch.escopo}` : '',
                              arrasto?.ensaioId === ch.ensaio_id ? 'arrastando' : '']
                              .filter(Boolean).join(' ')}
                            disabled={!podeProgramar}
                            title={`${ch.metodo_codigo} — ${ch.metodo_nome}\n${ch.cliente ?? 'sem cliente'}`
                              + `\n${ch.os_numero ? `OS ${ch.os_numero}` : 'sem OS'} · ${dias(ch.duracao_sugerida)}`
                              + `\namostra ${nomeDaAmostra(daApi(ch))}`}
                            onPointerDown={(e) => {
                              if (!podeProgramar || e.button !== 0) return;
                              /* 0191: captura o ponteiro — o botão continua recebendo
                                 o movimento mesmo quando o cursor sai dele, e nenhuma
                                 seleção de texto entra no meio do gesto. */
                              try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* sem captura */ }
                              arrastou.current = false;
                              setArrasto({
                                tipo: 'bandeja', ensaioId: ch.ensaio_id,
                                origemX: e.clientX, origemY: e.clientY,
                                dx: 0, dy: 0, chip: ch,
                              });
                            }}
                            onClick={() => {
                              if (arrasto || arrastou.current) { arrastou.current = false; return; }
                              setNovaProg({
                                ensaioId: ch.ensaio_id, equipamentoId: '',
                                frenteId: ch.classe === 'trivial' ? (ch.frente_sugerida_id ?? '') : '',
                                data: ch.data_prevista ? ch.data_prevista.slice(0, 10) : hoje, duracao: '', fds: false, obs: '',
                              });
                            }}
                          >
                            <span className="gm-pega" aria-hidden="true" />
                            <span className="gm-chip-l1">
                              <span className="mini gm-chip-met">{ch.metodo_codigo}</span>
                              <span className="gm-chip-am"><NomeDaAmostra amostra={daApi(ch)} emLinha /></span>
                            </span>
                            <span className="gm-chip-cli">{ch.cliente ?? 'sem cliente'}</span>
                            <small className="det">
                              {ch.os_numero ? `OS ${ch.os_numero}` : 'sem OS'}
                              {' · '}{dias(ch.duracao_sugerida)}
                              {ch.classe === 'trivial' ? ' · frente' : ''}
                            </small>
                            {!ch.categorias_aceitas?.length && ch.classe !== 'trivial' && (
                              <small className="det aviso-chip">
                                {ch.grupo ?? 'grupo'} · sem máquina declarada
                              </small>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              {podeProgramar && (
                <div className="gm-bandeja-pe">
                  <p className="det">
                    Arraste um ensaio até a faixa de um equipamento ou de uma
                    frente — ou marque vários e programe de uma vez.
                  </p>
                  <button type="button" className="botao gm-lote-botao"
                    disabled={marcadosChips.length === 0}
                    onClick={() => setEmLote(true)}>
                    {marcadosChips.length === 0 ? 'Agendar marcados'
                      : `Agendar ${marcadosChips.length} marcado(s)`}
                  </button>
                </div>
              )}
            </>
          )}
        </aside>
      </div>

      {/*
        * O fantasma (onda 200): o chip que se arrasta segue o ponteiro,
        * sem receber eventos — o alvo continua sendo o que está EMBAIXO
        * dele (`elementFromPoint`). Sem isso o chip ficava parado na
        * bandeja e o gesto parecia não pegar.
        */}
      {/* 0198: o fantasma da BARRA que mira outra máquina. */}
      {arrasto?.tipo === 'mover' && arrasto.barra?.equipamento_id && alvo && alvo !== arrasto.barra.equipamento_id
        && Math.abs(arrasto.dy) > 8 && createPortal(
        <div className={`gm-fantasma gm-fantasma-barra${arrasto.barra.escopo ? ` escopo-${arrasto.barra.escopo}` : ''} com-alvo`}
          style={{ left: arrasto.origemX + arrasto.dx + 14, top: arrasto.origemY + arrasto.dy + 14 }}
          aria-hidden="true">
          <span className="mini gm-chip-met">{arrasto.barra.metodo_codigo}</span>
          {' '}<NomeDaAmostra amostra={daApi(arrasto.barra)} emLinha />
          <small className="gm-fantasma-alvo">
            {(() => { const m = dados.maquinas.find((xm) => xm.equipamento_id === alvo);
              if (!m) return '';
              if (!serve(m, { categorias_aceitas: arrasto.barra!.categorias_aceitas, grupo: arrasto.barra!.grupo })) return `✕ ${m.codigo_patrimonial} não faz este ensaio`;
              if (m.apto === false) return `✕ ${m.codigo_patrimonial} impedida`;
              return `→ ${m.codigo_patrimonial}${Math.round(arrasto.dx / LARGURA_DIA) ? ` · ${fmtData(paraIso(emDias(arrasto.barra!.inicio_previsto) + Math.round(arrasto.dx / LARGURA_DIA)))}` : ' · mesma data'}`; })()}
          </small>
        </div>,
        document.body,
      )}

      {arrasto?.tipo === 'bandeja' && arrasto.chip && createPortal(
        /* 0191: por portal no <body> — `position: fixed` dentro de um
           ancestral com transform vira relativo a ele, e o fantasma sumia. */
        <div className={`gm-fantasma${arrasto.chip.escopo ? ` escopo-${arrasto.chip.escopo}` : ''}${alvo || alvoFrente ? ' com-alvo' : ''}`}
          style={{ left: arrasto.origemX + arrasto.dx + 14, top: arrasto.origemY + arrasto.dy + 14 }}
          aria-hidden="true">
          <span className="mini gm-chip-met">{arrasto.chip.metodo_codigo}</span>
          {' '}<NomeDaAmostra amostra={daApi(arrasto.chip)} emLinha />
          <small className="det"> · {arrasto.chip.cliente ?? 'sem cliente'} · {arrasto.chip.os_numero ? `OS ${arrasto.chip.os_numero}` : 'sem OS'}</small>
          <small className="gm-fantasma-alvo">
            {alvo
              ? (() => { const m = dados.maquinas.find((xm) => xm.equipamento_id === alvo);
                  if (!m) return '';
                  if (arrasto.chip!.classe === 'trivial') return `✕ ensaio trivial vai para uma frente`;
                  if (!serve(m, arrasto.chip)) return `✕ ${m.codigo_patrimonial} não faz este ensaio`;
                  return previa
                    ? `→ ${m.codigo_patrimonial} · começa ${fmtData(previa.inicio)}${previa.por === 'fila' ? ' (fila da máquina)' : ''}`
                    : `→ ${m.codigo_patrimonial}`; })()
              : alvoFrente
                ? (() => { const f = dados.frentes.find((xf) => xf.id === alvoFrente);
                    if (!f) return '';
                    if (arrasto.chip!.classe !== 'trivial') return `✕ ensaio especial não vai para frente`;
                    if (!serveFrente(f, arrasto.chip)) return `✕ ${arrasto.chip!.metodo_codigo} é da frente ${arrasto.chip!.frente_sugerida_codigo ?? '?'}`;
                    return previa
                      ? `→ frente ${f.codigo} · começa ${fmtData(previa.inicio)}${previa.por === 'fila' ? ' (capacidade da frente)' : ''}`
                      : `→ frente ${f.codigo}`; })()
                : arrasto.chip.classe === 'trivial'
                  ? 'solte sobre a linha de uma frente'
                  : 'solte sobre a linha de um equipamento'}
          </small>
        </div>,
        document.body,
      )}

      {resolvendo && (
        <ResolverConflitos
          escopoInicial={resolvendo}
          maquinas={dados.maquinas}
          barras={dados.barras}
          aoFechar={() => setResolvendo(null)}
          aoAplicar={async (movs, motivoLote) => {
            setRecado(null);
            const r = await aplicarConflitos({ movimentos: movs, motivo: motivoLote });
            setResolvendo(null);
            setDesfazer(null);
            setDesfazerLote(r.feitos);
            setOk(`${r.movidos} ensaio(s) reprogramado(s)`
              + (r.conflitos_restantes ? ` — restam ${r.conflitos_restantes} em conflito (fixos ou fora do escopo).` : ' — sem conflitos.'));
            await carregar();
          }} />
      )}

      {capacidades && (
        <CapacidadeDasFrentes lista={dados.frentes} aoFechar={() => setCapacidades(false)}
          aoGravar={async (id, n) => {
            await gravarCapacidade(id, n);
            await carregar();
          }} />
      )}

      {duracoes && (
        <DuracoesPadrao lista={duracoes} aoFechar={() => setDuracoes(null)}
          aoGravar={async (id, diasPadrao) => {
            const r = await gravarDuracao(id, diasPadrao);
            setDuracoes((l) => l ? l.map((d) => d.id === id
              ? { ...d, duracao_padrao_dias: r.duracao_padrao_dias, duracao_prevista: r.duracao_prevista }
              : d) : l);
            await carregar();
          }} />
      )}

      {/* ── programar vários de uma vez (266) ─────────────────────── */}
      {emLote && (
        <ProgramarEmLote
          chips={marcadosChips} dados={dados} hoje={hoje} feriados={feriadosSet}
          aoFechar={() => setEmLote(false)}
          aoProgramar={async (itens) => {
            setRecado(null); setOk(null);
            setGravando(true);
            let feitos = 0;
            try {
              for (const it of itens) {
                // eslint-disable-next-line no-await-in-loop
                await programar({
                  ensaioId: it.ensaioId, inicio: it.inicio,
                  ...(it.equipamentoId ? { equipamentoId: it.equipamentoId } : { frenteId: it.frenteId }),
                  motivo: 'Agendado em lote no cronograma.', origem: 'lote',
                });
                feitos += 1;
              }
              setOk(`${feitos} ensaio(s) agendado(s) em lote.`);
              setDesfazer(null);
              setMarcados(new Set());
              setEmLote(false);
            } catch (e) {
              setRecado(`${feitos} de ${itens.length} programado(s); parou em: ${mensagemDoErro(e)}`);
            } finally {
              setGravando(false);
              await carregar();
            }
          }} />
      )}

      {/* ── o motivo da mudança de data ───────────────────────────── */}
      {pedindoMotivo && (
        <Dialogo aberto titulo="Reprogramar"
          aoFechar={() => setPedindoMotivo(null)}>
          <p>
            <strong>{rotuloDe(pedindoMotivo.barra)}</strong>
            {' '}sai de {fmtData(pedindoMotivo.barra.inicio_previsto)} para{' '}
            <strong>{fmtData(pedindoMotivo.novoInicio)}</strong>
            {pedindoMotivo.novoFim && <> mantendo o fim em {fmtData(pedindoMotivo.novoFim)}</>}
            {pedindoMotivo.equipamentoId && ', e muda de máquina'}.
          </p>
          <p className="sub">
            O §8 pede o motivo do atraso registrado. A programação anterior
            não é apagada — ela vira a revisão {pedindoMotivo.barra.revisao_atual + 1}.
          </p>
          <Campo rotulo="Motivo" value={motivo} autoFocus
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Prensa em manutenção, amostra atrasada…" />
          <div className="acoes">
            <button type="button" className="botao discreto"
              onClick={() => setPedindoMotivo(null)}>Cancelar</button>
            <button type="button" className="botao"
              disabled={motivo.trim().length < 3 || gravando}
              onClick={() => { void confirmarMotivo(); }}>
              Reprogramar
            </button>
          </div>
        </Dialogo>
      )}

      {/* ── nova programação ──────────────────────────────────────── */}
      {novaProg && dados && (
        <NovaProgramacao
          dados={dados} estado={novaProg} hoje={hoje}
          aoMudar={setNovaProg}
          aoFechar={() => setNovaProg(null)}
          aoCriar={async (corpoNovo) => {
            try {
              setGravando(true);
              await programar(corpoNovo);
              setDesfazer(null);
              setOk('Programado.');
              setNovaProg(null);
              await carregar();
            } catch (e) { setRecado(mensagemDoErro(e)); }
            finally { setGravando(false); }
          }} />
      )}
    </div>
  );
}

/*
 * O fundo da faixa: os dias, com fim de semana, feriado e hoje.
 *
 * 266: até aqui eram TODAS as colunas como elementos — 90 dias × cada
 * linha do Gantt passava de três mil `div`s, e rolar a página travava a
 * aba. Agora o dia útil e o fio entre os dias são o fundo do próprio
 * `.gm-fundo` (um gradiente que repete a cada dia); só vira elemento o
 * que é exceção: sábado, domingo, feriado, hoje e a coluna sob o arrasto.
 */
function Fundo({ colunas, inicio, hojeDias, largura, feriados, colunaAlvo }: {
  colunas: number; inicio: number; hojeDias: number; largura: number;
  /** 0195: feriados da bancada (`AAAA-MM-DD`) e a coluna sob o arrasto. */
  feriados: Set<string>; colunaAlvo: number | null;
}) {
  const excecoes: { i: number; classe: string }[] = [];
  for (let i = 0; i < colunas; i += 1) {
    const d = inicio + i;
    const dow = new Date(d * DIA_MS).getUTCDay();
    const cl = [dow === 0 || dow === 6 ? 'fds' : '',
      feriados.has(paraIso(d)) ? 'feriado' : '',
      d === hojeDias ? 'hoje' : '',
      colunaAlvo === i ? 'alvo' : ''].filter(Boolean).join(' ');
    if (cl) excecoes.push({ i, classe: cl });
  }
  return (
    <div className="gm-fundo" style={{ width: colunas * largura }}>
      {excecoes.map(({ i, classe }) => (
        <div key={i} className={`gm-col ${classe}`}
          style={{ left: i * largura, width: largura }} />
      ))}
    </div>
  );
}

// ── Nova programação ───────────────────────────────────────────────

function NovaProgramacao({ dados, estado, hoje, aoMudar, aoFechar, aoCriar }: {
  dados: Resposta;
  estado: { ensaioId: string; equipamentoId: string; frenteId: string; data: string;
    duracao: string; fds: boolean; obs: string };
  hoje: string;
  aoMudar(e: typeof estado): void;
  aoFechar(): void;
  aoCriar(corpo: Parameters<typeof programar>[0]): Promise<void>;
}) {
  const chip = dados.bandeja.find((c) => c.ensaio_id === estado.ensaioId);
  const dur = Number(estado.duracao) || chip?.duracao_sugerida || 1;
  const maq = dados.maquinas.find((m) => m.equipamento_id === estado.equipamentoId);
  const aceita = chip?.categorias_aceitas;
  const inapta = !!(maq && chip && !serve(maq, chip));
  const [fim, setFim] = useState<string | null>(null);

  /* O fim, pela conta do banco (feriados da bancada). */
  useEffect(() => {
    let vivo = true;
    if (!estado.data) { setFim(null); return; }
    contaDatas({ inicio: estado.data, duracao: dur, fds: estado.fds })
      .then((r) => { if (vivo) setFim(r.fim); })
      .catch(() => { if (vivo) setFim(null); });
    return () => { vivo = false; };
  }, [estado.data, dur, estado.fds]);

  const compativeis = [...dados.maquinas]
    .filter((m) => serve(m, chip))
    .sort((a, b) => (a.livre_em ?? '9999').localeCompare(b.livre_em ?? '9999'));
  /* 0197: ensaio trivial vai para uma FRENTE, não para uma máquina. */
  const trivial = chip?.classe === 'trivial';
  const frentesServem = dados.frentes.filter((f) => serveFrente(f, chip));

  /*
   * 267: «Programar» é decidir, na OS, que ensaios a amostra faz.
   * «Agendar» é pôr esse ensaio numa máquina ou frente, num dia. O
   * cronograma só agenda — o botão e o diálogo dizem isso.
   */
  return (
    <Dialogo aberto titulo="Agendar ensaio" aoFechar={aoFechar}>
      <label className="campo">
        <span>Ensaio</span>
        <select value={estado.ensaioId} autoFocus
          onChange={(e) => aoMudar({ ...estado, ensaioId: e.target.value })}>
          <option value="">Selecione o ensaio</option>
          {dados.bandeja.map((c) => (
            <option key={c.ensaio_id} value={c.ensaio_id}>
              {c.metodo_codigo} · {nomeDaAmostra(daApi(c, true))}
            </option>
          ))}
        </select>
      </label>
      {trivial ? (
        <label className="campo">
          <span>Frente de serviço</span>
          <select value={estado.frenteId}
            onChange={(e) => aoMudar({ ...estado, frenteId: e.target.value, equipamentoId: '' })}>
            <option value="">Sem frente (fica na bandeja)</option>
            {frentesServem.map((f) => (
              <option key={f.id} value={f.id}>
                {f.codigo} — {f.nome} · {f.capacidade_dia}/dia
              </option>
            ))}
          </select>
          {chip?.frente_sugerida_codigo && (
            <small className="det">{chip.metodo_codigo} é da frente {chip.frente_sugerida_codigo}.</small>
          )}
        </label>
      ) : (
      <label className="campo">
        <span>Equipamento</span>
        <select value={estado.equipamentoId}
          onChange={(e) => aoMudar({ ...estado, equipamentoId: e.target.value, frenteId: '' })}>
          <option value="">Sem equipamento (fica na bandeja)</option>
          {compativeis.map((m) => (
            <option key={m.equipamento_id} value={m.equipamento_id}>
              {m.codigo_patrimonial} — {m.nome}
              {m.livre_em === hoje ? ' · livre hoje'
                : m.livre_em ? ` · livre em ${fmtData(m.livre_em)}` : ' · impedida'}
              {m.apto === false ? ' · INAPTA' : ''}
            </option>
          ))}
        </select>
        {chip && aceita && aceita.length > 0 && (
          <small className="det">Mostrando só os equipamentos do grupo «{chip.grupo}».</small>
        )}
      </label>
      )}
      {!trivial && compativeis.length > 0 && (
        <div className="gm-disponibilidade">
          <small className="det">Próxima disponibilidade (mais cedo primeiro)</small>
          <ul>
            {compativeis.slice(0, 4).map((m) => (
              <li key={m.equipamento_id}>
                <button type="button" className="link-linha"
                  onClick={() => aoMudar({ ...estado, equipamentoId: m.equipamento_id,
                    data: m.livre_em && m.livre_em > estado.data ? m.livre_em : estado.data })}>
                  {m.codigo_patrimonial}
                </button>
                <span className="det">
                  {m.livre_em === hoje ? 'livre hoje'
                    : m.livre_em ? `livre em ${fmtData(m.livre_em)}` : 'impedida'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="gm-dupla">
        <Campo rotulo="Início programado" type="date" value={estado.data}
          onChange={(e) => aoMudar({ ...estado, data: e.target.value })} />
        <Campo rotulo="Duração (dias)" type="number" min={0.5} step={0.5}
          value={estado.duracao}
          placeholder={chip ? String(chip.duracao_sugerida) : ''}
          onChange={(e) => aoMudar({ ...estado, duracao: e.target.value })} />
      </div>
      <p className="sub">
        {fim ? <>O fim programado é <b>{fmtData(fim)}</b>. </> : null}
        {estado.fds
          ? 'Sábados, domingos e feriados contam na duração.'
          : 'Sábados, domingos e feriados de Blumenau não contam na duração.'}
        {' '}Quando o técnico começar a lançar, a faixa do realizado aparece por
        baixo — a barra programada fica onde está, e este primeiro previsto
        vira a referência inicial.
      </p>
      <label className="campo marca">
        <input type="checkbox" checked={estado.fds}
          onChange={(e) => aoMudar({ ...estado, fds: e.target.checked })} />
        <span>
          <strong>Considerar finais de semana e feriados</strong>
          <br />O adensamento de doze estágios de 24 h é a exceção —
          ele prende a prensa no sábado.
        </span>
      </label>
      <Campo rotulo="Observações" value={estado.obs}
        onChange={(e) => aoMudar({ ...estado, obs: e.target.value })} />
      {inapta && maq && chip && (
        <Aviso tipo="erro">
          {chip.metodo_codigo} é do grupo «{chip.grupo ?? '—'}», que roda
          em {aceita!.join(' ou ')} — e {maq.codigo_patrimonial} é {maq.categoria}.
        </Aviso>
      )}
      <div className="acoes">
        <button type="button" className="botao discreto" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="botao"
          aria-disabled={!estado.ensaioId || !estado.data || inapta}
          title={!estado.ensaioId ? 'Escolha o ensaio — sem ele a barra não sabe de que amostra é.'
            : !estado.data ? 'A barra precisa de um começo.'
            : inapta ? 'A máquina não serve para este grupo de método.' : undefined}
          onClick={() => {
            if (!estado.ensaioId || !estado.data || inapta) return;
            void aoCriar({
              ensaioId: estado.ensaioId, inicio: estado.data,
              duracaoDias: estado.duracao ? dur : null,
              equipamentoId: trivial ? null : (estado.equipamentoId || null),
              frenteId: trivial ? (estado.frenteId || null) : null,
              correNoFimDeSemana: estado.fds,
              observacoes: estado.obs || undefined,
              motivo: 'Agendado pelo cronograma de máquinas.',
              origem: 'formulario',
            });
          }}>
          Agendar
        </button>
      </div>
    </Dialogo>
  );
}

// ── Programar vários de uma vez (266) ────────────────────────────

/*
 * Os ensaios marcados na bandeja vão para UM destino — uma máquina ou uma
 * frente — a partir de um dia. Na máquina, um depois do outro (cada um
 * começa no dia útil seguinte ao fim do anterior); na frente, todos no
 * mesmo dia, que é como a capacidade da frente conta. A conta é a mesma
 * da prévia do arrasto (RN-196: dia útil, nunca hoje), e o servidor
 * continua sendo quem diz se cabe: cada gravação passa pela porta única
 * (`programar`) com a origem «lote» e o motivo na revisão.
 */
function ProgramarEmLote({ chips, dados, hoje, feriados, aoFechar, aoProgramar }: {
  chips: NaBandeja[]; dados: Resposta; hoje: string; feriados: Set<string>;
  aoFechar(): void;
  aoProgramar(itens: { ensaioId: string; inicio: string; equipamentoId?: string; frenteId?: string }[]): Promise<void>;
}) {
  const misto = new Set(chips.map((ch) => ch.classe)).size > 1;
  const trivial = chips[0]?.classe === 'trivial';
  const maquinas = trivial ? [] : dados.maquinas.filter((m) => m.apto !== false
    && chips.every((ch) => serve(m, ch)));
  const frentes = trivial ? dados.frentes.filter((f) => chips.every((ch) => serveFrente(f, ch))) : [];
  const amanha = proximoDiaUtil(paraIso(emDias(hoje) + 1), feriados);
  const primeiroDe = (id: string) => (trivial ? amanha
    : (maquinas.find((m) => m.equipamento_id === id)?.proximo_inicio ?? amanha));
  const [destino, setDestino] = useState(trivial ? (frentes[0]?.id ?? '') : (maquinas[0]?.equipamento_id ?? ''));
  const [inicio, setInicio] = useState(() => primeiroDe(destino));
  const [gravando, setGravando] = useState(false);

  const plano: { ch: NaBandeja; inicio: string; fim: string }[] = [];
  let cursor = proximoDiaUtil(inicio || amanha, feriados);
  for (const ch of chips) {
    const dur = Math.max(1, Number(ch.duracao_sugerida) || 1);
    const ini = trivial ? proximoDiaUtil(inicio || amanha, feriados) : cursor;
    const fim = fimDaPrevia(ini, dur, feriados);
    plano.push({ ch, inicio: ini, fim });
    if (!trivial) cursor = proximoDiaUtil(paraIso(emDias(fim) + 1), feriados);
  }

  const semDestino = trivial ? frentes.length === 0 : maquinas.length === 0;

  return (
    <Dialogo aberto titulo={`Agendar ${chips.length} ensaio(s)`} aoFechar={aoFechar}>
      {misto ? (
        <Aviso tipo="erro">
          Há ensaios especiais e triviais marcados juntos. Especial vai para
          uma máquina e trivial para uma frente — marque só de um tipo.
        </Aviso>
      ) : semDestino ? (
        <Aviso tipo="erro">
          {trivial
            ? 'Nenhuma frente atende todos os ensaios marcados.'
            : 'Nenhuma máquina apta faz todos os ensaios marcados.'}
          {' '}Desmarque os que não combinam e tente de novo.
        </Aviso>
      ) : (
        <>
          <div className="gm-dupla">
            <label className="campo">
              <span>{trivial ? 'Frente' : 'Máquina'}</span>
              <select value={destino} aria-label={trivial ? 'Frente do lote' : 'Máquina do lote'}
                onChange={(e) => { setDestino(e.target.value); setInicio(primeiroDe(e.target.value)); }}>
                {trivial
                  ? frentes.map((f) => <option key={f.id} value={f.id}>{f.codigo} · {f.nome}</option>)
                  : maquinas.map((m) => (
                    <option key={m.equipamento_id} value={m.equipamento_id}>
                      {m.codigo_patrimonial}{m.livre_em ? ` · livre em ${fmtData(m.livre_em)}` : ''}
                    </option>))}
              </select>
            </label>
            <Campo rotulo="A partir de" type="date" value={inicio}
              onChange={(e) => setInicio(e.target.value || amanha)} />
          </div>
          <p className="sub">
            {trivial
              ? 'Todos no mesmo dia útil — a capacidade da frente é contada por dia.'
              : 'Um depois do outro, cada um no dia útil seguinte ao fim do anterior.'}
          </p>
          <table className="dados gm-lote">
            <thead><tr><th scope="col">Ensaio</th><th scope="col">Início</th><th scope="col">Fim</th></tr></thead>
            <tbody>
              {plano.map(({ ch, inicio: ini, fim }) => (
                <tr key={ch.ensaio_id}>
                  <td><b>{ch.metodo_codigo}</b> · <NomeDaAmostra amostra={daApi(ch, true)} emLinha /></td>
                  <td className="mono">{fmtData(ini)}</td>
                  <td className="mono">{fmtData(fim)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div className="acoes">
        <button type="button" className="botao discreto" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="botao"
          disabled={misto || semDestino || !destino || gravando}
          onClick={() => {
            setGravando(true);
            void aoProgramar(plano.map((pl) => ({
              ensaioId: pl.ch.ensaio_id, inicio: pl.inicio,
              ...(trivial ? { frenteId: destino } : { equipamentoId: destino }),
            }))).finally(() => setGravando(false));
          }}>
          Agendar {chips.length}
        </button>
      </div>
    </Dialogo>
  );
}

// ── O detalhe da barra ─────────────────────────────────────────────

const prof = (a: string | null, b: string | null) => {
  if (a == null && b == null) return null;
  const f = (v: string | null) => (v == null ? '?'
    : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }));
  return `${f(a)} – ${f(b)} m`;
};

function DetalheDaBarra({ barra: b, maquinas, podeProgramar, aoFechar, aoDesalocar, aoGravar, aoResolver }: {
  barra: Barra; maquinas: Maquina[]; podeProgramar: boolean;
  aoFechar(): void; aoDesalocar(): void;
  /** 0198: abre «Resolver conflitos» no escopo deste ensaio. */
  aoResolver?(): void;
  aoGravar(corpo: Parameters<typeof programar>[0]): Promise<void>;
}) {
  const [revisoes, setRevisoes] = useState<Revisao[] | null>(null);
  const [referencia, setReferencia] = useState<number>(0);
  const [editando, setEditando] = useState(false);
  const [f, setF] = useState({
    inicio: b.inicio_previsto,
    fim: b.fim_previsto ?? b.inicio_previsto,
    duracao: String(num(b.duracao_dias)),
    fds: b.corre_no_fim_de_semana,
    equipamentoId: b.equipamento_id ?? '',
    motivo: '',
  });
  /* Qual campo manda: o último que a pessoa mexeu decide o que é
     recalculado (§7: mudar o início mantém a duração; mudar o fim mantém
     o início; mudar a duração mantém o início). */
  const [manda, setManda] = useState<'inicio' | 'fim' | 'duracao'>('inicio');
  const [previa, setPrevia] = useState<{ fim: string | null; duracao: number | null } | null>(null);

  useEffect(() => {
    let vivo = true;
    verRevisoes(b.ensaio_id)
      .then((r) => { if (vivo) setRevisoes(r); })
      .catch(() => { if (vivo) setRevisoes([]); });
    return () => { vivo = false; };
  }, [b.ensaio_id]);

  useEffect(() => {
    if (!editando || !f.inicio) return;
    let vivo = true;
    const pedido = manda === 'fim'
      ? contaDatas({ inicio: f.inicio, fim: f.fim, fds: f.fds })
      : contaDatas({ inicio: f.inicio, duracao: Number(f.duracao) || 1, fds: f.fds });
    pedido.then((r) => { if (vivo) setPrevia(r); }).catch(() => { if (vivo) setPrevia(null); });
    return () => { vivo = false; };
  }, [editando, f.inicio, f.fim, f.duracao, f.fds, manda]);

  const mudouData = f.inicio !== b.inicio_previsto;
  const ref = revisoes?.find((r) => r.numero === referencia) ?? null;
  const refInicio = referencia === 0 ? b.inicio_base : ref?.inicio_novo ?? null;
  const refFim = referencia === 0 ? b.fim_base : ref?.fim_novo ?? null;

  /*
   * 266: o painel do ensaio abre POR CIMA da grade, não como janela
   * modal — a bandeja continua à direita e a grade continua viva, para
   * editar um ensaio e seguir arrastando outros. Esc fecha, como fechava
   * a janela.
   */
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [aoFechar]);

  const titulo = `${b.metodo_codigo} · ${nomeDaAmostra(daApi(b))}`;
  return (
    <aside className="gm-painel" aria-label={titulo}>
      <div className="cabeca-dialogo gm-painel-cab">
        <h2>{titulo}</h2>
        <button type="button" className="fechar-dialogo" aria-label="Fechar" title="Fechar"
          onClick={aoFechar}>✕</button>
      </div>
      <div className="gm-painel-corpo">
      <p className="sub">
        {b.os_numero && <>OS {b.os_numero} · </>}
        {b.cliente ?? '—'}
        {b.obra_nome && <> · {b.obra_nome}</>}
        {prof(b.profundidade_inicial, b.profundidade_final) && (
          <> · {prof(b.profundidade_inicial, b.profundidade_final)}</>
        )}
      </p>

      <div className="gm-detalhe-grade">
        <section>
          <h3 className="gm-h3">Programado{b.revisao_atual > 0 && <small className="det"> · revisão {b.revisao_atual}</small>}</h3>
          {!editando ? (
            <dl className="ficha-curta">
              <dt>Início</dt><dd>{fmtData(b.inicio_previsto)}</dd>
              <dt>Fim</dt><dd>{b.fim_previsto ? fmtData(b.fim_previsto) : '—'}</dd>
              <dt>Duração</dt>
              <dd>
                {dias(b.duracao_dias)} <small className="det">{b.duracao_unidade}</small>
                {b.duracao_do_metodo && (
                  <small className="det">a duração que o método declara</small>
                )}
              </dd>
              {b.frente_id
                ? <><dt>Frente</dt><dd>{b.frente_codigo ?? '—'}{b.frente_nome && <small className="det">{b.frente_nome}</small>}</dd></>
                : <><dt>Máquina</dt><dd>{b.equipamento_codigo ?? '—'}{b.equipamento_nome && <small className="det">{b.equipamento_nome}</small>}</dd></>}
              <dt>Programado por</dt><dd>{b.programado_por_nome ?? '—'}</dd>
              {b.folga_dias != null && (
                <>
                  <dt>Folga</dt>
                  <dd>{b.folga_dias} dia(s) contra o prazo {b.critico && <Selo tom="critico">Caminho crítico</Selo>}</dd>
                </>
              )}
            </dl>
          ) : (
            <div className="gm-editar">
              <div className="gm-dupla">
                <Campo rotulo="Início" type="date" value={f.inicio}
                  onChange={(e) => { setManda('inicio'); setF({ ...f, inicio: e.target.value }); }} />
                <Campo rotulo="Fim" type="date" value={f.fim}
                  onChange={(e) => { setManda('fim'); setF({ ...f, fim: e.target.value }); }} />
              </div>
              <div className="gm-dupla">
                <Campo rotulo="Duração (dias)" type="number" min={0.5} step={0.5} value={f.duracao}
                  onChange={(e) => { setManda('duracao'); setF({ ...f, duracao: e.target.value }); }} />
                {b.frente_id ? (
                  <label className="campo">
                    <span>Frente</span>
                    <input value={`${b.frente_codigo ?? ''} — ${b.frente_nome ?? ''}`} readOnly />
                  </label>
                ) : (
                  <label className="campo">
                    <span>Máquina</span>
                    <select value={f.equipamentoId}
                      onChange={(e) => setF({ ...f, equipamentoId: e.target.value })}>
                      <option value="">sem máquina</option>
                      {maquinas.map((m) => (
                        <option key={m.equipamento_id} value={m.equipamento_id}>
                          {m.codigo_patrimonial} — {m.nome}{m.apto === false ? ' · INAPTA' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <label className="campo marca">
                <input type="checkbox" checked={f.fds}
                  onChange={(e) => setF({ ...f, fds: e.target.checked })} />
                <span><strong>Contar fim de semana e feriados</strong></span>
              </label>
              <p className="sub">
                {manda === 'fim'
                  ? <>Mudar o fim mantém o início: a duração passa a <b>{previa?.duracao != null ? dias(previa.duracao) : '…'}</b>.</>
                  : manda === 'duracao'
                    ? <>Mudar a duração mantém o início: o fim passa a <b>{previa?.fim ? fmtData(previa.fim) : '…'}</b>.</>
                    : <>Mudar o início mantém a duração: o fim passa a <b>{previa?.fim ? fmtData(previa.fim) : '…'}</b>.</>}
                {' '}A conta é do calendário da bancada (feriados de Blumenau).
              </p>
              {mudouData && (
                <Campo rotulo="Motivo da mudança de data" value={f.motivo}
                  onChange={(e) => setF({ ...f, motivo: e.target.value })}
                  placeholder="Prensa em manutenção, amostra atrasada…" />
              )}
            </div>
          )}
        </section>

        <section>
          <h3 className="gm-h3">Realizado</h3>
          <dl className="ficha-curta">
            <dt>Início real</dt>
            <dd>{b.inicio_real ? fmtData(b.inicio_real) : '—'}
              {b.inicio_real && <small className="det">primeiro dado bruto lançado</small>}</dd>
            <dt>Fim real</dt>
            <dd>
              {b.fim_real ? fmtData(b.fim_real) : (b.em_execucao_aberta ? 'em execução' : '—')}
              {b.fim_real && (
                <small className="det">
                  {b.fim_real_origem === 'ficha_completa'
                    ? 'a ficha ficou completa'
                    : 'marco posterior à execução (ensaio anterior ao gatilho da ficha completa)'}
                </small>
              )}
            </dd>
            {b.em_execucao_aberta && b.previsao_fim && (
              <>
                <dt>Previsão de fim</dt>
                <dd>{fmtData(b.previsao_fim)} <small className="det">previsão: a duração programada contada do início real</small></dd>
              </>
            )}
            {b.fim_real && b.desvio_termino_dias != null && (
              <>
                <dt>Desvio</dt>
                <dd>{b.desvio_termino_dias > 0 ? '+' : ''}{b.desvio_termino_dias} dia(s)
                  <small className="det">fim real − fim programado</small></dd>
              </>
            )}
            {!b.fim_real && b.atraso_atual_dias > 0 && (
              <>
                <dt>Atraso atual</dt>
                <dd><Selo tom="critico">{b.atraso_atual_dias} dia(s) após o fim programado</Selo></dd>
              </>
            )}
            {(b.reaberturas ?? 0) > 0 && (
              <>
                <dt>Reaberturas</dt>
                <dd>{b.reaberturas} {b.fim_real_corrigido && <small className="det">ficha completa de novo em {fmtData(b.fim_real_corrigido)}</small>}</dd>
              </>
            )}
            <dt>Executor</dt><dd>{b.executor ?? '—'}</dd>
            <dt>Situação</dt>
            <dd>
              <Selo tom={b.percentual === 100 ? 'ok' : (b.atrasado || b.atraso_atual_dias > 0) ? 'critico' : 'andamento'}>
                {SITUACAO[b.situacao] ?? b.situacao}
              </Selo>
              <small className="det">{b.percentual}% da escada — posição, não esforço</small>
            </dd>
          </dl>
        </section>
      </div>

      <section>
        <h3 className="gm-h3">Revisões do programado</h3>
        {revisoes == null ? <p className="sub">Carregando…</p>
          : revisoes.length === 0 ? <p className="sub">Sem revisões registradas.</p>
            : (
              <>
                <p className="sub">
                  Régua tracejada no Gantt: comparar com{' '}
                  <select value={referencia} onChange={(e) => setReferencia(Number(e.target.value))}>
                    {revisoes.map((r) => (
                      <option key={r.id} value={r.numero}>
                        {r.numero === 0 ? 'a referência inicial' : `a revisão ${r.numero}`}
                      </option>
                    ))}
                  </select>
                  {refInicio && (
                    <> — {fmtData(refInicio)}{refFim ? ` → ${fmtData(refFim)}` : ''}</>
                  )}
                  <br /><small className="det">Na tela, a régua mostra sempre a referência inicial; a comparação escolhida vale para este painel.</small>
                </p>
                <table className="dados gm-revisoes">
                  <thead>
                    <tr><th>Rev.</th><th>Quando</th><th>Quem</th><th>De onde</th><th>Início</th><th>Duração</th><th>Máquina</th><th>Motivo</th></tr>
                  </thead>
                  <tbody>
                    {revisoes.map((r) => (
                      <tr key={r.id} className={r.numero === referencia ? 'ativa' : ''}>
                        <td>{r.numero === 0 ? 'inicial' : r.numero}</td>
                        <td>{fmtDataHora(r.datahora)}</td>
                        <td>{r.usuario_nome ?? '—'}</td>
                        <td>{ORIGEM[r.origem]}</td>
                        <td>
                          {r.numero === 0 ? (r.inicio_novo ? fmtData(r.inicio_novo) : '—')
                            : r.inicio_anterior !== r.inicio_novo
                              ? <>{r.inicio_anterior ? fmtData(r.inicio_anterior) : '—'} → <b>{r.inicio_novo ? fmtData(r.inicio_novo) : '—'}</b></>
                              : (r.inicio_novo ? fmtData(r.inicio_novo) : '—')}
                        </td>
                        <td>
                          {r.numero === 0 ? (r.duracao_nova != null ? dias(r.duracao_nova) : 'do método')
                            : String(r.duracao_anterior ?? '') !== String(r.duracao_nova ?? '')
                              ? <>{r.duracao_anterior != null ? dias(r.duracao_anterior) : 'do método'} → <b>{r.duracao_nova != null ? dias(r.duracao_nova) : 'do método'}</b></>
                              : (r.duracao_nova != null ? dias(r.duracao_nova) : 'do método')}
                        </td>
                        <td>
                          {r.numero === 0 ? (r.equipamento_novo_codigo ?? '—')
                            : r.equipamento_anterior !== r.equipamento_novo
                              ? <>{r.equipamento_anterior_codigo ?? '—'} → <b>{r.equipamento_novo_codigo ?? '—'}</b></>
                              : (r.equipamento_novo_codigo ?? '—')}
                        </td>
                        <td className="det">{r.motivo ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
      </section>

      {b.em_conflito && (
        <Aviso tipo="atencao" titulo="Conflito de ocupação">
          Esta máquina tem {b.simultaneos ?? '?'} ensaios ao
          mesmo tempo e {b.postos ?? 1} posto(s). O sistema
          mostra e não recusa — se a máquina realmente segura mais de
          um corpo de prova, corrija o número de postos no cadastro
          dela.
          {podeProgramar && aoResolver && (
            <>
              {' '}
              <button type="button" className="botao discreto" onClick={aoResolver}>
                Resolver este conflito
              </button>
            </>
          )}
        </Aviso>
      )}

      <div className="acoes">
        <Link className="botao discreto" to={`/ensaio/${b.ensaio_id}/situacao`}>
          Abrir a ficha
        </Link>
        {podeProgramar && !editando && (
          <>
            <button type="button" className="botao discreto"
              disabled={b.situacao === 'aprovado'}
              onClick={() => setEditando(true)}>
              Editar datas
            </button>
            <button type="button" className="botao discreto" onClick={aoDesalocar}>
              {b.frente_id ? 'Tirar da frente' : 'Tirar da máquina'}
            </button>
          </>
        )}
        {podeProgramar && editando && (
          <>
            <button type="button" className="botao discreto" onClick={() => setEditando(false)}>
              Cancelar
            </button>
            <button type="button" className="botao"
              aria-disabled={!f.inicio || (mudouData && f.motivo.trim().length < 3)}
              title={mudouData && f.motivo.trim().length < 3 ? 'Mudar a data pede o motivo.' : undefined}
              onClick={() => {
                if (!f.inicio || (mudouData && f.motivo.trim().length < 3)) return;
                void aoGravar({
                  ensaioId: b.ensaio_id,
                  inicio: f.inicio,
                  ...(manda === 'fim' ? { fim: f.fim } : { duracaoDias: Number(f.duracao) || null }),
                  equipamentoId: f.equipamentoId || null,
                  correNoFimDeSemana: f.fds,
                  motivo: f.motivo.trim() || undefined,
                  origem: 'formulario',
                });
              }}>
              Gravar
            </button>
          </>
        )}
      </div>
      </div>
    </aside>
  );
}


/**
 * As durações padrão por método (0191) — a tabela que o Humberto pediu:
 * «padronize uma quantidade de dias específica para cada tipo de ensaio».
 * Edita-se aqui, sem onda: é planejamento, não regra de ensaio. Método com
 * estágios cadastrados mostra a conta dos estágios e não aceita valor.
 */
function DuracoesPadrao({ lista, aoFechar, aoGravar }: {
  lista: Duracao[];
  aoFechar(): void;
  aoGravar(id: string, dias: number): Promise<void>;
}) {
  const [editando, setEditando] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const grupos = useMemo(() => {
    const g: { nome: string; itens: Duracao[] }[] = [];
    for (const d of lista) {
      const u = g[g.length - 1];
      if (u && u.nome === (d.grupo ?? '—')) u.itens.push(d);
      else g.push({ nome: d.grupo ?? '—', itens: [d] });
    }
    return g;
  }, [lista]);

  const gravar = async (d: Duracao) => {
    const txt = editando[d.id];
    if (txt === undefined) return;
    const n = Number(String(txt).replace(',', '.'));
    if (!(n > 0)) { setErro(`${d.codigo}: a duração tem de ser um número de dias maior que zero.`); return; }
    if (String(n) === String(Number(d.duracao_padrao_dias ?? ''))) {
      setEditando((e) => { const c = { ...e }; delete c[d.id]; return c; });
      return;
    }
    setSalvando(d.id); setErro(null);
    try {
      await aoGravar(d.id, n);
      setEditando((e) => { const c = { ...e }; delete c[d.id]; return c; });
    } catch (e) { setErro(mensagemDoErro(e)); }
    finally { setSalvando(null); }
  };

  return (
    <Dialogo aberto titulo="Durações padrão por método" aoFechar={aoFechar}>
      <p className="sub">
        Dias que a bancada costuma levar em cada ensaio especial. É o que o
        chip sugere ao ser arrastado e o que a barra recebe quando ninguém
        informa outra duração. Vale para os ensaios programados <b>daqui em
        diante</b>; os já programados guardam a duração que receberam.
      </p>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <table className="dados gm-duracoes">
        <thead><tr><th>Método</th><th>Nome</th><th className="num">Dias</th></tr></thead>
        <tbody>
          {grupos.map((g) => (
            <Fragment key={g.nome}>
              <tr className="gm-duracoes-grupo"><th colSpan={3}>{g.nome}</th></tr>
              {g.itens.map((d) => (
                <tr key={d.id}>
                  <td className="mono">{d.codigo}</td>
                  <td>{d.nome}</td>
                  <td className="num">
                    {d.estagios > 0 ? (
                      <span title={`${d.estagios} estágio(s) cadastrado(s): a duração é a soma deles`}>
                        {d.duracao_prevista} d <small className="det">(estágios)</small>
                      </span>
                    ) : (
                      <input type="number" min={0.5} step={0.5} className="cel"
                        aria-label={`Duração padrão de ${d.codigo}, em dias`}
                        value={editando[d.id] ?? (d.duracao_padrao_dias != null ? String(Number(d.duracao_padrao_dias)) : '')}
                        placeholder="1"
                        disabled={salvando === d.id}
                        onChange={(e) => setEditando((m) => ({ ...m, [d.id]: e.target.value }))}
                        onBlur={() => { void gravar(d); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                    )}
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
      <div className="acoes">
        <button type="button" className="botao" onClick={aoFechar}>Fechar</button>
      </div>
    </Dialogo>
  );
}


/**
 * 0197: a capacidade de cada frente, em ensaios por dia — «a confirmar»
 * pela Testesolo (doc 182 §3-bis). Edita-se aqui, como as durações:
 * é planejamento, não regra de ensaio.
 */
function CapacidadeDasFrentes({ lista, aoFechar, aoGravar }: {
  lista: Frente[];
  aoFechar(): void;
  aoGravar(id: string, capacidadeDia: number): Promise<void>;
}) {
  const [editando, setEditando] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);

  const gravar = async (f: Frente) => {
    const txt = editando[f.id];
    if (txt === undefined) return;
    const n = Number(String(txt).replace(',', '.'));
    if (!(n > 0)) { setErro(`${f.codigo}: a capacidade tem de ser um número de ensaios por dia maior que zero.`); return; }
    if (n === f.capacidade_dia) {
      setEditando((e) => { const c = { ...e }; delete c[f.id]; return c; });
      return;
    }
    setSalvando(f.id); setErro(null);
    try {
      await aoGravar(f.id, n);
      setEditando((e) => { const c = { ...e }; delete c[f.id]; return c; });
    } catch (e) { setErro(mensagemDoErro(e)); }
    finally { setSalvando(null); }
  };

  return (
    <Dialogo aberto titulo="Capacidade das frentes" aoFechar={aoFechar}>
      <p className="sub">
        Quantos ensaios cada frente executa por dia. É o que a faixa da
        frente compara com a carga do dia e o que decide onde um chip
        arrastado para ela começa quando os dias estão cheios. Os números
        iniciais são uma estimativa a confirmar pela Testesolo.
      </p>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <table className="dados gm-duracoes">
        <thead><tr><th>Frente</th><th>Nome</th><th className="num">Ensaios/dia</th></tr></thead>
        <tbody>
          {lista.map((f) => (
            <tr key={f.id}>
              <td className="mono">{f.codigo}</td>
              <td>{f.nome} <small className="det">{FAMILIA[f.familia]}</small></td>
              <td className="num">
                <input type="number" min={0.5} step={0.5} className="cel"
                  aria-label={`Capacidade da frente ${f.codigo}, em ensaios por dia`}
                  value={editando[f.id] ?? String(f.capacidade_dia)}
                  disabled={salvando === f.id}
                  onChange={(e) => setEditando((m) => ({ ...m, [f.id]: e.target.value }))}
                  onBlur={() => { void gravar(f); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="acoes">
        <button type="button" className="botao" onClick={aoFechar}>Fechar</button>
      </div>
    </Dialogo>
  );
}


/**
 * 0198 · Resolver conflitos — o diálogo.
 *
 * Escopo (tudo / esta máquina / este ensaio) → «Calcular» pede a proposta
 * ao servidor (nada gravado) → a lista de movimentos, cada um com caixa
 * para desmarcar → «Aplicar» grava só os marcados, numa transação, com o
 * motivo. A regra da proposta (RN-200) está na função do banco; aqui só
 * se mostra e se escolhe.
 */
function ResolverConflitos({ escopoInicial, maquinas, barras, aoFechar, aoAplicar }: {
  escopoInicial: { equipamentoId?: string; ensaioId?: string };
  maquinas: Maquina[];
  barras: Barra[];
  aoFechar(): void;
  aoAplicar(movimentos: { ensaioId: string; inicioAtual: string; inicioNovo: string }[], motivo: string): Promise<void>;
}) {
  const [tipo, setTipo] = useState<'todos' | 'maquina' | 'ensaio'>(
    escopoInicial.ensaioId ? 'ensaio' : escopoInicial.equipamentoId ? 'maquina' : 'todos');
  const [equipamentoId, setEquipamentoId] = useState(escopoInicial.equipamentoId ?? '');
  const [ensaioId, setEnsaioId] = useState(escopoInicial.ensaioId ?? '');
  const [proposta, setProposta] = useState<PropostaConflitos | null>(null);
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set());
  const [motivo, setMotivo] = useState('');
  const [calculando, setCalculando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const emConflito = barras.filter((b) => b.em_conflito && b.equipamento_id);
  const maquinasComConflito = maquinas.filter((m) => emConflito.some((b) => b.equipamento_id === m.equipamento_id));
  const nomeMaq = (id: string) => maquinas.find((m) => m.equipamento_id === id)?.codigo_patrimonial ?? '—';

  const calcular = async () => {
    setErro(null); setCalculando(true); setProposta(null);
    try {
      const p = await proporConflitos(
        tipo === 'maquina' ? { equipamentoId } : tipo === 'ensaio' ? { ensaioId } : {});
      setProposta(p);
      setEscolhidos(new Set(p.movimentos.map((m) => m.ensaio_id)));
    } catch (e) { setErro(mensagemDoErro(e)); }
    finally { setCalculando(false); }
  };

  const alternar = (id: string) => setEscolhidos((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const aplicar = async () => {
    if (!proposta) return;
    const movs = proposta.movimentos.filter((m) => escolhidos.has(m.ensaio_id))
      .map((m) => ({ ensaioId: m.ensaio_id, inicioAtual: m.inicio_atual, inicioNovo: m.inicio_novo }));
    if (movs.length === 0) return;
    setErro(null); setAplicando(true);
    try {
      await aoAplicar(movs, motivo.trim() || 'Resolução de conflitos de postos (proposta do sistema).');
    } catch (e) { setErro(mensagemDoErro(e)); setAplicando(false); }
  };

  const podeCalcular = tipo === 'todos' || (tipo === 'maquina' && !!equipamentoId) || (tipo === 'ensaio' && !!ensaioId);
  const porMaquina = useMemo(() => {
    const g: { codigo: string; itens: Movimento[] }[] = [];
    for (const m of proposta?.movimentos ?? []) {
      const u = g[g.length - 1];
      if (u && u.codigo === m.equipamento_codigo) u.itens.push(m);
      else g.push({ codigo: m.equipamento_codigo, itens: [m] });
    }
    return g;
  }, [proposta]);

  return (
    <Dialogo aberto titulo="Resolver conflitos de postos" aoFechar={aoFechar}>
      <p className="sub">
        Quem já começou não se move. Entre os que se cruzam, <b>fica quem tem
        menos folga</b> contra o prazo da OS (caminho crítico primeiro); os
        outros vão para a próxima vaga <b>da mesma máquina</b>. O sistema
        propõe — nada é gravado até você aplicar.
      </p>

      <div className="gm-escopo" role="radiogroup" aria-label="Escopo">
        <label className="campo marca">
          <input type="radio" name="escopo" checked={tipo === 'todos'} onChange={() => { setTipo('todos'); setProposta(null); }} />
          <span>Todos os ensaios <small className="det">({emConflito.length} em conflito, {maquinasComConflito.length} máquina(s))</small></span>
        </label>
        <label className="campo marca">
          <input type="radio" name="escopo" checked={tipo === 'maquina'} onChange={() => { setTipo('maquina'); setProposta(null); }} />
          <span>Só um equipamento</span>
        </label>
        {tipo === 'maquina' && (
          <select value={equipamentoId} aria-label="Equipamento"
            onChange={(e) => { setEquipamentoId(e.target.value); setProposta(null); }}>
            <option value="">Escolha a máquina</option>
            {maquinasComConflito.map((m) => (
              <option key={m.equipamento_id} value={m.equipamento_id}>
                {m.codigo_patrimonial} · {emConflito.filter((b) => b.equipamento_id === m.equipamento_id).length} em conflito
              </option>
            ))}
          </select>
        )}
        <label className="campo marca">
          <input type="radio" name="escopo" checked={tipo === 'ensaio'} onChange={() => { setTipo('ensaio'); setProposta(null); }} />
          <span>Só um ensaio <small className="det">(os outros ficam onde estão)</small></span>
        </label>
        {tipo === 'ensaio' && (
          <select value={ensaioId} aria-label="Ensaio"
            onChange={(e) => { setEnsaioId(e.target.value); setProposta(null); }}>
            <option value="">Escolha o ensaio</option>
            {emConflito.map((b) => (
              <option key={b.ensaio_id} value={b.ensaio_id}>
                {b.metodo_codigo} · {nomeDaAmostra(daApi(b, true))} · {nomeMaq(b.equipamento_id!)} · {fmtData(b.inicio_previsto)}
              </option>
            ))}
          </select>
        )}
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {!proposta ? (
        <div className="acoes">
          <button type="button" className="botao discreto" onClick={aoFechar}>Cancelar</button>
          <button type="button" className="botao" disabled={!podeCalcular || calculando}
            onClick={() => { void calcular(); }}>
            {calculando ? 'Calculando…' : 'Calcular a proposta'}
          </button>
        </div>
      ) : proposta.movimentos.length === 0 ? (
        <>
          <Aviso tipo="atencao" titulo="Nada a mover">
            {proposta.antes.ensaios === 0
              ? 'Não há conflito neste escopo.'
              : proposta.antes.fixos > 0
                ? `Os ${proposta.antes.ensaios} ensaios em conflito incluem ${proposta.antes.fixos} que já começaram — esses não se movem, e o resto cabe onde está.`
                : 'Os ensaios em conflito cabem onde estão ou não podem ser movidos.'}
          </Aviso>
          <div className="acoes">
            <button type="button" className="botao" onClick={aoFechar}>Fechar</button>
          </div>
        </>
      ) : (
        <>
          <p className="sub gm-proposta-resumo">
            <b>{proposta.movimentos.length}</b> movimento(s) para desfazer{' '}
            <b>{proposta.antes.ensaios}</b> conflito(s) em <b>{proposta.antes.maquinas}</b> máquina(s)
            {proposta.antes.fixos > 0 && <> · {proposta.antes.fixos} já começado(s), fixo(s)</>}.
            Desmarque o que não deve andar.
          </p>
          <table className="dados gm-proposta">
            <thead>
              <tr><th></th><th>Ensaio</th><th>OS</th><th>Folga</th><th>De</th><th>Para</th><th className="num">Adia</th></tr>
            </thead>
            <tbody>
              {porMaquina.map((g) => (
                <Fragment key={g.codigo}>
                  <tr className="gm-duracoes-grupo"><th colSpan={7}>{g.codigo}</th></tr>
                  {g.itens.map((m) => (
                    <tr key={m.ensaio_id} className={escolhidos.has(m.ensaio_id) ? '' : 'apagada'} data-movimento={m.ensaio_id}>
                      <td>
                        <input type="checkbox" checked={escolhidos.has(m.ensaio_id)}
                          aria-label={`Mover ${m.metodo_codigo} · ${nomeDaAmostra(daApi(m))}`}
                          onChange={() => alternar(m.ensaio_id)} />
                      </td>
                      <td><b>{m.metodo_codigo}</b> · <NomeDaAmostra amostra={daApi(m)} emLinha /></td>
                      <td>{m.os_numero ?? '—'}</td>
                      <td>{m.folga_dias == null ? <small className="det">sem prazo</small>
                        : <span className={m.critico ? 'gm-folga critica' : ''}>{m.folga_dias >= 0 ? '+' : ''}{m.folga_dias}d</span>}</td>
                      <td>{fmtData(m.inicio_atual)} → {fmtData(m.fim_atual)}</td>
                      <td><b>{fmtData(m.inicio_novo)}</b> → {fmtData(m.fim_novo)}</td>
                      <td className="num">{m.dias_adiados} d</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          <Campo rotulo="Motivo (vai para a revisão de cada ensaio)" value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Resolução de conflitos de postos (proposta do sistema)." />
          <div className="acoes">
            <button type="button" className="botao discreto" onClick={aoFechar}>Cancelar</button>
            <button type="button" className="botao discreto" disabled={calculando} onClick={() => { void calcular(); }}>Recalcular</button>
            <button type="button" className="botao" disabled={aplicando || escolhidos.size === 0}
              onClick={() => { void aplicar(); }}>
              {aplicando ? 'Gravando…' : `Aplicar ${escolhidos.size} movimento(s)`}
            </button>
          </div>
        </>
      )}
    </Dialogo>
  );
}
